import { getActiveUserIdentity, getSessionEmail } from "@/lib/userSession";
import { getLocalhostAccessPreviewMode } from "@/lib/adminAccess";

const DEFAULT_PRODUCTION_API = 'https://optra-backend.onrender.com';

const isLocalPreviewHost = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = String(window.location.hostname || '').toLowerCase();
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost')
  );
};

const isLoopbackHostname = (hostname) => {
  const normalized = String(hostname || '').toLowerCase();
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '::1' ||
    normalized.endsWith('.localhost')
  );
};

// Environment-aware API configuration
/**
 * POST /api/assessments/start(-sync) answers 401 {"detail": "Please sign in to run your assessment."}
 * for a caller without a verified session: every assessment belongs to a signed-in account. The
 * detail is surfaced as the error message (name AuthRequiredError) so the progress page can show it
 * as is, not as a raw HTTP dump.
 */
function buildAssessmentAuthError(errorText) {
  let detail = '';
  try {
    detail = String(JSON.parse(errorText)?.detail || '').trim();
  } catch {
    detail = '';
  }
  const authError = new Error(detail || 'Please sign in to run your assessment.');
  authError.name = 'AuthRequiredError';
  authError.status = 401;
  return authError;
}

export const getApiBaseUrl = () => {
  const raw =
    typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL;
  const trimmed = typeof raw === 'string' ? raw.trim().replace(/\/$/, '') : '';

  if (isLocalPreviewHost()) {
    return '';
  }

  try {
    if (import.meta.env?.DEV) {
      // No explicit base → use same-origin `/api` so Vite proxies to Render (avoids CORS on LAN IPs, e.g. 192.168.x.x).
      if (!trimmed) {
        return '';
      }
      // .env often sets production URL for convenience; in dev that triggers browser CORS from non-localhost hosts.
      if (trimmed === DEFAULT_PRODUCTION_API.replace(/\/$/, '')) {
        return '';
      }
      return trimmed;
    }
  } catch {
    /* import.meta unavailable */
  }

  return trimmed || DEFAULT_PRODUCTION_API;
};

const IS_DEV = (() => {
  try {
    return (
      typeof import.meta !== 'undefined' &&
      import.meta.env &&
      Boolean(import.meta.env.DEV)
    );
  } catch (error) {
    console.warn('Unable to read import.meta.env for DEV detection:', error);
    // In browser builds without a polyfilled process, fall back to false
    if (typeof process !== 'undefined' && process.env) {
      return process.env.NODE_ENV !== 'production';
    }
    return false;
  }
})();

const debugLog = (...args) => {
  if (IS_DEV) {
    console.log(...args);
  }
};

const debugWarn = (...args) => {
  if (IS_DEV) {
    console.warn(...args);
  }
};

const debugError = (...args) => {
  if (IS_DEV) {
    console.error(...args);
  }
};

// Session and token management
class SessionManager {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.tokenHash = this.generateTokenHash();
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  generateTokenHash() {
    // In a real app, this would be a hash of the user's JWT token
    // For now, generate a consistent hash for the session
    return `token_${this.sessionId}`;
  }

  getSessionId() {
    return this.sessionId;
  }

  getTokenHash() {
    return this.tokenHash;
  }

  // Update token hash when user logs in
  updateTokenHash(newToken) {
    this.tokenHash = this.hashToken(newToken);
  }

  hashToken(token) {
    // Simple hash function - in production, use a proper hashing library
    let hash = 0;
    for (let i = 0; i < token.length; i++) {
      const char = token.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `hash_${Math.abs(hash)}`;
  }
}

// Global session manager
const sessionManager = new SessionManager();

const parseEnvBoolean = (value) => {
  if (typeof value === 'string') {
    return ['true', '1', 'yes', 'on'].includes(value.toLowerCase());
  }
  return Boolean(value);
};

const USE_LOCAL_BACKEND = (() => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_USE_LOCAL_BACKEND !== undefined) {
      return parseEnvBoolean(import.meta.env.VITE_USE_LOCAL_BACKEND);
    }
  } catch (error) {
    // Only log error message, not the entire env object to avoid parse errors during build
    // Vite tries to statically replace import.meta.env, which can cause parse errors
    const errorMessage = (error && error.message) ? error.message : String(error);
    if (IS_DEV) {
      debugWarn('Unable to read VITE_USE_LOCAL_BACKEND:', errorMessage);
    }
  }

  if (typeof process !== 'undefined' && process.env) {
    const fallbackFlag = process.env.VITE_USE_LOCAL_BACKEND || process.env.NEXT_PUBLIC_USE_LOCAL_BACKEND;
    if (fallbackFlag !== undefined) {
      return parseEnvBoolean(fallbackFlag);
    }
  }

  return false;
})();

const API_BASE_URL = getApiBaseUrl();

/** One shared in-flight request / short-lived answer for GET /v1/dashboard/client-ops/record/me. */
let myClientOpsRecordCache = null;
const MY_CLIENT_OPS_RECORD_TTL_MS = 10_000;
/** The same for GET /v1/dashboard/chats/unread-count. */
let chatUnreadCountCache = null;
const CHAT_UNREAD_COUNT_TTL_MS = 5_000;

class ApiService {
  constructor() {
    this.baseURL = getApiBaseUrl();
    this.pendingRequests = new Map(); // Track pending requests to prevent duplicates
    this.listDashboardClientsCache = new Map();
    this.rateLimitCooldowns = new Map(); // endpoint-scope cooldown after 429
    this.backendCooldowns = new Map(); // per-backend cooldown after connection failures
    this.localBackendDisabledUntil = 0; // disable localhost:8000 probing after connection failures
    this.localConfiguredBackendDisabledUntil = 0; // disable configured localhost:5001 backend after connection failures
    this.unsupportedEndpointUntil = new Map(); // endpoint-scope cooldown after 405
    this.dashboardWorkspaceEnsureUnsupported = false;
    this.dashboardApiUnsupported = false;
    this.grantSimulatorFormsCache = null;
    this.grantSimulatorFormsPending = null;
  }

  getBackendCooldownKey(url) {
    if (typeof url !== 'string' || !url.trim()) return '';
    try {
      return new URL(url).origin;
    } catch {
      return url.trim();
    }
  }

  isBackendOnCooldown(url) {
    const key = this.getBackendCooldownKey(url);
    if (!key) return false;
    return Date.now() < Number(this.backendCooldowns.get(key) || 0);
  }

  markBackendOnCooldown(url, durationMs, reason = 'connection failure') {
    const key = this.getBackendCooldownKey(url);
    if (!key) return;
    const until = Date.now() + Math.max(1000, Number(durationMs) || 0);
    this.backendCooldowns.set(key, until);
    debugWarn(`⏸️ Cooling down ${key} for ${Math.round((until - Date.now()) / 1000)}s after ${reason}.`);
  }

  getLocalPreviewHeaders() {
    try {
      const mode = String(getLocalhostAccessPreviewMode() || "").trim().toLowerCase();
      if (mode === "admin" || mode === "customer") {
        return { "X-Optra-Local-Preview": mode };
      }
    } catch {
      // no-op
    }
    return {};
  }

  getChatActorHeaders() {
    const identity = getActiveUserIdentity();
    const sessionEmailRaw = String(identity?.email || getSessionEmail() || "").trim().toLowerCase();
    const clerkUserId = String(identity?.userId || "").trim();
    const clerkName = String(identity?.name || "").trim();
    const previewMode = String(getLocalhostAccessPreviewMode() || "").trim().toLowerCase();
    let sessionEmail = sessionEmailRaw;
    // In localhost preview mode we force canonical identities to keep admin/customer
    // mapped to the same deterministic chat accounts across tabs and refreshes.
    if (previewMode === "admin") {
      sessionEmail = "contact@optra-labs.com";
    } else if (previewMode === "customer") {
      sessionEmail = "customer@localhost.test";
    }
    return {
      ...(sessionEmail ? { 'X-User-Email': sessionEmail } : {}),
      ...(clerkUserId ? { 'X-Clerk-User-Id': clerkUserId } : {}),
      ...(clerkName ? { 'X-User-Name': clerkName } : {}),
      ...this.getLocalPreviewHeaders(),
    };
  }

  isChatDebugEnabled() {
    try {
      if (typeof window === 'undefined') return false;
      return String(window.localStorage?.getItem('chatDebug') || '').trim() === '1';
    } catch {
      return false;
    }
  }

  getChatDebugHeaders(action = 'unknown') {
    const traceId = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const headers = {
      'X-Chat-Trace-Id': traceId,
      'X-Chat-Action': String(action || 'unknown'),
    };
    if (this.isChatDebugEnabled()) {
      headers['X-Optra-Chat-Debug'] = '1';
      debugLog('[chat-debug] request', { action, traceId });
    }
    return headers;
  }

  getRateLimitScope(endpoint) {
    if (typeof endpoint !== 'string') return 'global';
    if (endpoint.startsWith('/v1/dashboard/')) return '/v1/dashboard/*';
    return endpoint.split('?')[0] || 'global';
  }

  getUnsupportedScope(endpoint, method = 'GET') {
    const verb = String(method || 'GET').toUpperCase();
    if (typeof endpoint !== 'string') return 'global';
    if (endpoint.startsWith('/v1/dashboard/')) return `${verb}:/v1/dashboard/*`;
    return `${verb}:${endpoint.split('?')[0] || 'global'}`;
  }

  // Prevent duplicate requests to the same endpoint
  async deduplicatedRequest(endpoint, options = {}) {
    const requestKey = `${endpoint}-${JSON.stringify(options)}`;
    
      debugLog(`🔑 Request key: ${requestKey}`);
      debugLog(`📋 Current pending requests:`, Array.from(this.pendingRequests.keys()));
    
    // If a request is already pending, wait for it
    if (this.pendingRequests.has(requestKey)) {
        debugLog(`🔄 Request already pending for ${endpoint}, waiting for result...`);
      return this.pendingRequests.get(requestKey);
    }
    
    // Create new request
      debugLog(`🆕 Creating new request for ${endpoint}`);
    const requestPromise = this.request(endpoint, options);
    
    // Store the promise
    this.pendingRequests.set(requestKey, requestPromise);
      debugLog(`💾 Stored request in pending requests`);
    
    try {
      const result = await requestPromise;
        debugLog(`✅ Request completed successfully`);
      return result;
    } finally {
      // Clean up when done
      this.pendingRequests.delete(requestKey);
        debugLog(`🧹 Cleaned up pending request`);
    }
  }

  // Generate document hash for caching and deduplication
  generateDocumentHash(data) {
    const hashInput = JSON.stringify(data);
    return this.sha256(hashInput);
  }

  generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    let timestamp = Date.now();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = (timestamp + Math.random() * 16) % 16 | 0;
      timestamp = Math.floor(timestamp / 16);
      if (char === 'x') {
        return random.toString(16);
      }
      return ((random & 0x3) | 0x8).toString(16);
    });
  }

  // Convert headcount range to integer for backend
  convertHeadcountToInteger(headcount) {
    if (typeof headcount === 'number') return headcount;
    if (typeof headcount === 'string') {
      switch (headcount) {
        case '1-10': return 5;
        case '11-50': return 30;
        case '51-200': return 125;
        case '201-500': return 350;
        case '500+': return 500;
        default: return 5;
      }
    }
    return 5; // default fallback
  }

  determineCompanySize(headcount) {
    if (typeof headcount === 'string') {
      switch (headcount) {
        case '1-10': return 'SME';
        case '11-50': return 'SME';
        case '51-200': return 'SME';
        case '201-500': return 'Large';
        case '500+': return 'Large';
        default: return 'SME';
      }
    }
    return 'SME';
  }

  convertRevenueToNumber(revenueRange) {
    if (typeof revenueRange === 'number') return revenueRange;
    if (typeof revenueRange === 'string') {
      switch (revenueRange) {
        case 'Under $1M': return 500000;
        case '$1M-$5M': return 3000000;
        case '$5M-$10M': return 7500000;
        case '$10M-$50M': return 30000000;
        case '$50M-$100M': return 75000000;
        case 'Over $100M': return 150000000;
        default: return 500000;
      }
    }
    return 500000;
  }

  convertTimelineToMonths(timeline) {
    if (typeof timeline === 'number') return timeline;
    if (typeof timeline === 'string') {
      switch (timeline) {
        case '1-3 months': return 2;
        case '3-6 months': return 4;
        case '6-12 months': return 9;
        case '1-2 years': return 18;
        case '2+ years': return 30;
        default: return 4;
      }
    }
    return 4;
  }

  // Simple SHA-256 implementation (in production, use a proper crypto library)
  sha256(str) {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `sha256_${Math.abs(hash)}`;
  }

  // Enhanced request method with better error handling
  async request(endpoint, options = {}) {
    const backendUrls = this.getBackendUrls(endpoint);
    if (!Array.isArray(backendUrls) || backendUrls.length === 0) {
      const emergencyBase = isLocalPreviewHost() || IS_DEV || USE_LOCAL_BACKEND ? '' : DEFAULT_PRODUCTION_API;
      debugWarn(`↪️ Request fallback engaged for ${endpoint}: using ${emergencyBase || "same-origin /api proxy"}.`);
      backendUrls.push(emergencyBase);
    }
    let lastError = null;
    const method = (options.method || 'GET').toUpperCase();
    const rateScope = this.getRateLimitScope(endpoint);
    const unsupportedScope = this.getUnsupportedScope(endpoint, method);
    const nowTs = Date.now();
    const cooldownUntil = Number(this.rateLimitCooldowns.get(rateScope) || 0);
    if (cooldownUntil > nowTs) {
      const retryAfterSeconds = Math.max(1, Math.ceil((cooldownUntil - nowTs) / 1000));
      const rateError = new Error(`Rate limit cooldown active. Retry in ${retryAfterSeconds}s`);
      rateError.name = 'RateLimitError';
      rateError.retryAfter = retryAfterSeconds;
      rateError.meta = {
        status: 429,
        retryAfter: retryAfterSeconds,
        scope: rateScope,
        cooldownUntil,
      };
      throw rateError;
    }
    const unsupportedUntil = Number(this.unsupportedEndpointUntil.get(unsupportedScope) || 0);
    if (unsupportedUntil > nowTs) {
      const unsupportedError = new Error('Endpoint temporarily marked unsupported by backend (405).');
      unsupportedError.name = 'MethodNotAllowedError';
      unsupportedError.meta = {
        status: 405,
        scope: unsupportedScope,
        unsupportedUntil,
      };
      throw unsupportedError;
    }
    
    debugLog(`🚀 Starting API request to ${endpoint}`);
    debugLog(`🔗 Backend URLs to try:`, backendUrls);
    
    const { timeoutMs: timeoutOverride, ...requestOptions } = options;
    
    for (let attemptIndex = 0; attemptIndex < backendUrls.length; attemptIndex += 1) {
      const baseUrl = backendUrls[attemptIndex];
      const url = `${baseUrl}${endpoint}`;
      const requestId = this.generateRequestId();
      const startTime = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      
      debugLog(`\n📡 Attempt ${attemptIndex + 1}/${backendUrls.length}: ${url}`);
      debugLog(`🆔 Request ID: ${requestId}`);
      
      const methodForHeaders = (requestOptions.method || method || 'GET').toUpperCase();
      const hasBody =
        requestOptions.body !== undefined &&
        requestOptions.body !== null &&
        methodForHeaders !== 'GET' &&
        methodForHeaders !== 'HEAD';
      const isFormDataBody = typeof FormData !== 'undefined' && requestOptions.body instanceof FormData;
      const baseHeaders = {
        'Accept': 'application/json',
      };
      // Avoid custom headers on simple GET requests to prevent local CORS preflight issues.
      if (hasBody && !isFormDataBody) {
        baseHeaders['Content-Type'] = 'application/json';
      }
      if (methodForHeaders !== 'GET' && methodForHeaders !== 'HEAD') {
        baseHeaders['X-Request-ID'] = requestId;
      }

      const defaultOptions = {
        headers: {
          ...baseHeaders,
          ...requestOptions.headers,
        },
        mode: 'cors',
        credentials: 'omit',
      };

      try {
        debugLog(`📤 Making ${method} request to: ${url}`);
        debugLog(`⚙️ Request options:`, { ...defaultOptions, ...requestOptions });

        const controller = new AbortController();
        // Same-origin `/api` in dev is proxied to Render (see vite.config.ts), not a fast local server.
        // Treat it like production for timeouts; otherwise generic calls use 5s and routinely abort.
        const isProxiedRemoteInDev = IS_DEV && baseUrl === '';
        const isProduction =
          baseUrl.includes('optra-backend.onrender.com') || isProxiedRemoteInDev;
        const isDetailedAnalysis = url.includes('detailed-analysis');
        const isReviewerEndpoint = /(^|\/)(review|reviewer)(\/|$)/.test(endpoint);
        const isApplicationGenerate = /application\/generate/.test(endpoint);
        const isDashboardEndpoint = endpoint.startsWith('/v1/dashboard/');
        const computedTimeoutMs = isReviewerEndpoint
          ? (isProduction ? 120000 : 30000) // 120s production, 30s localhost for review endpoint
          : isApplicationGenerate
          ? (isProduction ? 90000 : 60000) // 90s production, 60s localhost for application generation
          : isDashboardEndpoint
          ? (isProduction ? 30000 : 20000) // slow-but-succeeding local dashboard calls must not abort into retry storms
          : (isDetailedAnalysis ? 30000 : (isProduction ? 15000 : 5000));
        const timeoutMs = Number.isFinite(timeoutOverride) && Number(timeoutOverride) > 0
          ? Number(timeoutOverride)
          : computedTimeoutMs;

        const timeoutId = setTimeout(() => {
          debugLog(`⏰ Request timeout for ${url} after ${timeoutMs / 1000}s`);
          controller.abort();
        }, timeoutMs);

        const response = await fetch(url, {
          ...defaultOptions,
          ...requestOptions,
          headers: {
            ...defaultOptions.headers,
            ...requestOptions.headers,
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        const durationMs = Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - startTime);

        debugLog(`📥 Response received from ${url}:`);
        debugLog(`   Status: ${response.status} ${response.statusText}`);
        debugLog(`   Headers:`, Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
          let errorBody = '';
          try {
            errorBody = await response.text();
            if (response.status === 429) {
              debugWarn(`⏳ Rate limited response from ${url}:`, errorBody);
            } else if (response.status === 405) {
              debugWarn(`🚫 Endpoint not allowed on backend (${url}):`, errorBody);
            } else {
              debugError(`❌ Error response body from ${url}:`, errorBody);
            }
          } catch (error) {
            if (response.status === 429) {
              debugWarn(`⏳ Could not read rate-limit response body from ${url}:`, error);
            } else if (response.status === 405) {
              debugWarn(`🚫 Could not read 405 response body from ${url}:`, error);
            } else {
              debugError(`❌ Could not read error response body from ${url}:`, error);
            }
          }

          let errorData = null;
          try {
            errorData = errorBody ? JSON.parse(errorBody) : null;
          } catch (error) {
            debugError(`⚠️ Failed to parse error response JSON from ${url}:`, error);
          }

          if (response.status === 503 && isDashboardEndpoint) {
            const isUnreadCount = endpoint.includes('/v1/dashboard/chats/unread-count');
            const isMyClientOpsRecord = endpoint.includes('/v1/dashboard/client-ops/record/me');
            if (isUnreadCount || isMyClientOpsRecord) {
              const fallbackData = isUnreadCount
                ? { unread_count: 0, local_fallback: true, source: 'dashboard_503_fallback' }
                : { record: null, local_fallback: true, source: 'dashboard_503_fallback' };
              debugWarn(`⚠️ Falling back for ${url} after 503 storage-unavailable response.`);
              return {
                data: fallbackData,
                requestId,
                headers: Object.fromEntries(response.headers.entries()),
                status: response.status,
                durationMs,
                url,
              };
            }
          }

          const enhancedError = new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
          enhancedError.name = 'HttpError';
          enhancedError.meta = {
            url,
            method,
            status: response.status,
            statusText: response.statusText,
            requestId,
            durationMs,
            attempt: attemptIndex + 1,
          };
          if (errorData && typeof errorData === 'object') {
            enhancedError.meta.response = errorData;
            if (errorData.error?.message) {
              enhancedError.message = errorData.error.message;
            }
            if (errorData.error?.code) {
              enhancedError.meta.code = errorData.error.code;
            }
          }

          if (response.status === 422 && errorData?.detail) {
            const validationErrors = errorData.detail
              .map((err) => {
                const field = Array.isArray(err.loc) ? err.loc.join('.') : 'unknown field';
                return `${field}: ${err.msg}`;
              })
              .join(', ');
            enhancedError.message = `Validation error: ${validationErrors}`;
            enhancedError.meta.validationErrors = errorData.detail;
          }

          // Treat 408 (Request Timeout) from server as a timeout error
          if (response.status === 408) {
            enhancedError.name = 'TimeoutError';
            enhancedError.message = 'Request timed out - server may be slow to respond';
          }

          const detailText = String(
            errorData?.detail ||
            errorData?.error?.message ||
            ''
          ).toLowerCase();
          if (
            response.status === 429 ||
            detailText.includes('429: rate limit exceeded') ||
            detailText.includes('rate limit exceeded')
          ) {
            enhancedError.name = 'RateLimitError';
            enhancedError.message = 'Rate limit exceeded - slowing dashboard retries';
            const retryAfterSeconds =
              Number(response.headers.get('retry-after')) ||
              Number(errorData?.details?.retry_after) ||
              30;
            enhancedError.retryAfter = retryAfterSeconds;
            enhancedError.meta.retryAfter = retryAfterSeconds;
            this.rateLimitCooldowns.set(rateScope, Date.now() + (retryAfterSeconds * 1000));
          }

          if (enhancedError.name === 'RateLimitError') {
            enhancedError.name = 'RateLimitError';
            if (!enhancedError.meta.retryAfter) {
              this.rateLimitCooldowns.set(rateScope, Date.now() + 5000);
            }
          }
          if (response.status === 405) {
            enhancedError.name = 'MethodNotAllowedError';
            this.unsupportedEndpointUntil.set(unsupportedScope, Date.now() + 5 * 60 * 1000);
          }

          if (response.status === 429) {
            debugWarn(`⏳ Request rate-limited for ${url}`);
          } else if (response.status === 405) {
            debugWarn(`🚫 Request not allowed by backend for ${url}`);
          } else {
            debugError(`❌ Request failed for ${url}:`, enhancedError);
          }
          throw enhancedError;
        }

        let responseData;
        const contentType = response.headers.get('content-type') || '';

        try {
          if (contentType.includes('application/json')) {
            responseData = await response.json();
          } else {
            const textData = await response.text();
            responseData = textData;
          }
        } catch (parseError) {
          debugError(`⚠️ Failed to parse response as JSON, falling back to text for ${url}:`, parseError);
          const textData = await response.text();
          responseData = textData;
        }

        debugLog(`✅ Request successful for ${url}:`, responseData);

        return {
          data: responseData,
          requestId,
          headers: Object.fromEntries(response.headers.entries()),
          status: response.status,
          durationMs,
          url,
        };
      } catch (caughtError) {
        const durationMs = Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - startTime);
        let normalizedError = caughtError instanceof Error ? caughtError : new Error(String(caughtError));

        if (caughtError?.name === 'AbortError') {
          debugError(`⏰ Request timed out for ${url}`);
          normalizedError = new Error('Request timed out - server may be slow to respond');
          normalizedError.name = 'TimeoutError';
          normalizedError.cause = caughtError;
        }

        if (normalizedError.meta === undefined) {
          normalizedError.meta = {};
        }

        normalizedError.meta = {
          ...normalizedError.meta,
          url,
          method,
          requestId,
          durationMs,
          attempt: attemptIndex + 1,
          status: normalizedError.meta?.status ?? caughtError?.status,
        };

        // Definitive client errors (validation, auth, bad request) mean the
        // backend received and rejected the request — retrying against another
        // backend cannot succeed and must not leak requests to remote hosts.
        const failedStatus = Number(normalizedError.meta?.status || 0);
        const isDefinitiveClientError =
          failedStatus >= 400 &&
          failedStatus < 500 &&
          failedStatus !== 404 &&
          failedStatus !== 405 &&
          failedStatus !== 408 &&
          failedStatus !== 429;
        if (isDefinitiveClientError) {
          debugWarn(`🛑 Client error ${failedStatus} from ${url}; not retrying other backends.`);
          throw normalizedError;
        }

        if (
          normalizedError.name === 'RateLimitError' ||
          normalizedError.meta?.status === 429 ||
          normalizedError.name === 'MethodNotAllowedError' ||
          normalizedError.meta?.status === 405
        ) {
          if (normalizedError.name === 'MethodNotAllowedError' || normalizedError.meta?.status === 405) {
            debugLog(`🚫 Endpoint unsupported on this backend for ${url}`);
          } else {
            debugLog(`⏳ Rate limit exceeded for ${url} (will retry automatically)`);
          }
          normalizedError.meta.retryAfter = normalizedError.retryAfter || caughtError?.retryAfter;
          // Skip error logging for rate limits - they're handled with retries
          lastError = normalizedError;
          debugLog(`🔄 Trying next backend URL...`);
          continue;
        }

        const hasFallbackRemaining = attemptIndex < backendUrls.length - 1;
        if (hasFallbackRemaining) {
          debugWarn(`⚠️ Backend attempt failed (${url}), trying fallback backend...`);
        } else {
          debugError(`💥 Request failed for ${url}:`, normalizedError);
          debugError(`   Error type: ${normalizedError.name}`);
          debugError(`   Error message: ${normalizedError.message}`);
        }

        if (/^https?:\/\/(localhost|127\.0\.0\.1):8000/i.test(url) && normalizedError.name === 'TypeError') {
          this.localBackendDisabledUntil = IS_DEV ? 0 : Date.now() + 5 * 60 * 1000;
          debugWarn(
            IS_DEV
              ? '⛔ localhost:8000 connection refused (dev mode will keep retrying on the next request).'
              : '⛔ Disabling localhost:8000 backend attempts for 5 minutes (connection refused).'
          );
        }
        if ((url.startsWith('http://127.0.0.1:5001') || url.startsWith('http://localhost:5001')) && normalizedError.name === 'TypeError') {
          this.localConfiguredBackendDisabledUntil = IS_DEV ? 0 : Date.now() + 5 * 60 * 1000;
          debugWarn(
            IS_DEV
              ? '⛔ localhost:5001 connection refused (dev mode will keep retrying on the next request).'
              : '⛔ Disabling configured localhost:5001 backend attempts for 5 minutes (connection refused).'
          );
        }

        if (
          normalizedError.name === 'TypeError' ||
          normalizedError.name === 'TimeoutError' ||
          normalizedError.name === 'AbortError'
        ) {
          const localFailureCooldownMs = IS_DEV ? 30000 : 5 * 60 * 1000;
          if (/^https?:\/\/(localhost|127\.0\.0\.1)/i.test(url)) {
            this.markBackendOnCooldown(url, localFailureCooldownMs, normalizedError.name);
          }
        }

        lastError = normalizedError;
        debugLog(`🔄 Trying next backend URL...`);
        continue;
      }
    }

    debugLog('💥 All backend URLs failed. Last error:', lastError);

    if (
      lastError?.name === 'RateLimitError' ||
      lastError?.meta?.status === 429 ||
      lastError?.name === 'MethodNotAllowedError' ||
      lastError?.meta?.status === 405
    ) {
      throw lastError;
    }

    const lastDetail =
      lastError?.meta?.response?.detail ||
      lastError?.meta?.response?.error?.message ||
      lastError?.message ||
      'Unknown error';
    const errorMessage = `All backend URLs failed. Last error from ${lastError?.meta?.url || lastError?.url || 'unknown'}: ${lastDetail}`;
    const enhancedError = new Error(errorMessage);
    enhancedError.name = 'AllBackendsFailedError';
    enhancedError.meta = {
      triedUrls: backendUrls,
      lastError: lastError?.meta || null,
    };
    enhancedError.cause = lastError;

    throw enhancedError;
  }

  // Get list of backend URLs to try (development fallback)
  getBackendUrls(endpoint = '') {
    const urls = [];
    const configuredBase = getApiBaseUrl();
    const explicitLocalBase =
      typeof import.meta !== 'undefined' &&
      import.meta.env &&
      typeof import.meta.env.VITE_API_BASE_URL === 'string' &&
      /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(import.meta.env.VITE_API_BASE_URL.trim())
        ? import.meta.env.VITE_API_BASE_URL.trim().replace(/\/$/, '')
        : '';
    const normalizedEndpoint = String(endpoint || '');
    const isDashboardEndpoint = normalizedEndpoint.startsWith('/v1/dashboard/');

    if (typeof window !== 'undefined') {
      debugLog('🔍 Current hostname:', window.location.hostname);
      debugLog('🔍 Current port:', window.location.port);
    }

    const isLocalHost = typeof window !== 'undefined' &&
      isLoopbackHostname(window.location.hostname);
    const configuredLooksLocal =
      typeof configuredBase === 'string' &&
      /^https?:\/\/(localhost|127\.0\.0\.1):\d+\/?$/i.test(configuredBase);
    const preferredLocalBase = explicitLocalBase || (configuredLooksLocal ? configuredBase : '');
    const localRuntimeCandidates = isDashboardEndpoint
      ? [
          preferredLocalBase,
          'http://127.0.0.1:5001',
          'http://localhost:5001',
          'http://127.0.0.1:8000',
          'http://localhost:8000',
          configuredLooksLocal ? configuredBase : '',
        ]
      : [
          preferredLocalBase,
          'http://127.0.0.1:5001',
          'http://localhost:5001',
          'http://127.0.0.1:8000',
          'http://localhost:8000',
          configuredLooksLocal ? configuredBase : '',
          'http://127.0.0.1:8001',
          'http://localhost:8001',
        ];

    const addCandidate = (candidate, label = candidate) => {
      const normalized = typeof candidate === 'string' ? candidate.trim().replace(/\/$/, '') : '';
      if (normalized === '') {
        if (urls.includes('')) return;
        urls.push('');
        return;
      }
      if (!normalized || urls.includes(normalized)) return;
      if (this.isBackendOnCooldown(normalized)) {
        debugWarn(`⏭️ Skipping ${label} backend (temporarily disabled after connection failure).`);
        return;
      }
      urls.push(normalized);
    };

    const localBackendSuppressed = Date.now() < Number(this.localBackendDisabledUntil || 0);

    if (isLocalHost && (IS_DEV || USE_LOCAL_BACKEND)) {
      if (isDashboardEndpoint) {
        addCandidate('', 'same-origin /v1 proxy');
      }
      if (localBackendSuppressed) {
        debugWarn('⏭️ Skipping localhost:8000 backend (temporarily disabled after connection failure).');
      }
      for (const candidate of localRuntimeCandidates) {
        if (!candidate) continue;
        if (localBackendSuppressed && /^https?:\/\/(localhost|127\.0\.0\.1):8000\/?$/i.test(candidate)) {
          continue;
        }
        addCandidate(candidate, candidate);
      }
    }

    // IMPORTANT:
    // Do NOT auto-try localhost backend unless explicitly enabled.
    // In typical localhost frontend testing, same-origin `/api` proxy is preferred.

    if (!configuredLooksLocal && !this.isBackendOnCooldown(configuredBase) && !urls.includes(configuredBase)) {
      debugLog(`🔍 Adding configured backend: ${configuredBase}`);
      addCandidate(configuredBase, configuredBase);
    } else if (!configuredLooksLocal && this.isBackendOnCooldown(configuredBase)) {
      debugWarn(`⏭️ Skipping configured backend (${configuredBase}) temporarily after a connection failure.`);
    }

    // Never fall back to the hosted production API from a localhost runtime
    // (or when VITE_USE_LOCAL_BACKEND is set) — local failures must stay local.
    // Deployed builds are unaffected: their configured base is added above.

    if (urls.length === 0 && isLocalHost) {
      debugWarn(
        '↪️ No localhost backend candidates remain; falling back to same-origin /api proxy.'
      );
      urls.push('');
    }

    return urls;
  }

  // Generate unique request ID for tracking
  generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Generate unique trace ID for end-to-end tracking
  generateTraceId() {
    return `trace_${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
  }

  // Log structured API call with trace ID
  logApiCall(traceId, method, endpoint, data = null) {
    const logData = {
      traceId,
      timestamp: new Date().toISOString(),
      method,
      endpoint,
      baseURL: this.baseURL,
      dataPreview: data ? JSON.stringify(data).substring(0, 200) : null
    };
    debugLog(`[API-CALL] ${method} ${endpoint}`, logData);
    return logData;
  }

  // Log API response with trace ID
  logApiResponse(traceId, status, duration, data = null) {
    const logData = {
      traceId,
      timestamp: new Date().toISOString(),
      status,
      duration: `${duration}ms`,
      dataPreview: data ? JSON.stringify(data).substring(0, 200) : null
    };
    debugLog(`[API-RESPONSE] ${status}`, logData);
    return logData;
  }

  // Log API error with trace ID
  logApiError(traceId, error, duration = null) {
    const logData = {
      traceId,
      timestamp: new Date().toISOString(),
      error: error.message,
      status: error.response?.status,
      duration: duration ? `${duration}ms` : null,
      stack: error.stack?.substring(0, 300)
    };
    debugError(`[API-ERROR]`, logData);
    return logData;
  }

  // Grant Eligibility Assessment endpoints - using actual backend endpoints
  async submitEligibilityForm(formData) {
    debugLog('=== API SERVICE DEBUG ===');
    debugLog('Making request to /eligibility/run');
    debugLog('Original formData:', formData);
    
    // Transform form data to match backend expectations (flat EligibilityRequest structure)
    const eligibilityRequest = {
      company_name: formData.companyName || formData.company_profile?.companyName || 'Test Company',
      industry: formData.industry || formData.company_profile?.industry || 'technology',
      company_size: this.determineCompanySize(formData.headcount || formData.company_profile?.headcount || '1-10'),
      project_type: formData.project_type || formData.projectDetails?.projectType || 'digitalization',
      grant_name: formData.grantType || 'EDGE',
      annual_revenue: this.convertRevenueToNumber(formData.revenue_range || formData.company_profile?.revenue_range || 'Under $1M'),
      employee_count: this.convertHeadcountToInteger(formData.headcount || formData.company_profile?.headcount || '1-10'),
      project_budget: formData.project_budget || formData.projectDetails?.projectBudget || 50000,
      project_duration_months: this.convertTimelineToMonths(formData.project_timeline || formData.projectDetails?.projectTimeline || '3-6 months')
    };
    
    debugLog('Transformed request body:', eligibilityRequest);
    debugLog('Request payload:', JSON.stringify(eligibilityRequest, null, 2));
    
    try {
      debugLog('🚀 About to call deduplicatedRequest...');
      
      // Use the correct endpoint as specified by backend dev
      const response = await this.deduplicatedRequest('/eligibility/run', {
        method: 'POST',
        body: JSON.stringify(eligibilityRequest),
      });
      
      debugLog('✅ API Response successful:', response);
      return response.data; // Extract data from new response format
    } catch (error) {
      debugError('💥 API SERVICE ERROR ===');
      debugError('Error in submitEligibilityForm:', error);
      debugError('Error message:', error.message);
      debugError('Error type:', error.name);
      debugError('Request ID:', error.requestId);
      debugError('Full error object:', error);
      throw error;
    }
  }

  // Enhanced eligibility check with complete data structure
  async checkEligibility(completeData) {
    debugLog('🔍 Checking eligibility with complete data:', completeData);
    
    try {
      // Import the new API client
      const { submitEligibilityForm } = await import('../features/eligibility/api.js');
      
      // Use the new API integration
      const result = await submitEligibilityForm(completeData);
      
      if (result.success) {
        debugLog('✅ Eligibility check successful:', result.data);
        return { data: result.data };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      debugError('❌ Eligibility check failed:', error);
      throw error;
    }
  }

  // Enhanced eligibility analysis using the correct backend endpoint
  async getDetailedEligibilityAnalysis(formData) {
    debugLog('🔍 Getting detailed eligibility analysis:', formData);
    
    try {
      // Transform form data to match backend expectations (EligibilityRequest structure)
      const eligibilityRequest = {
        // Required fields
        company_name: formData.companyName || formData.company_profile?.companyName || 'Test Company',
        uen_number: formData.uen || formData.company_profile?.uen || '202012345A',
        year_of_incorporation: Math.min(parseInt(formData.incorporation_year || formData.company_profile?.incorporation_year || '2020'), 2024),
        primary_industry: formData.industry || formData.company_profile?.industry || 'technology',
        company_email: formData.email || formData.company_profile?.email || 'test@company.com',
        contact_phone: formData.phone || formData.company_profile?.phone || '+65 9123 4567',
        
        // Additional required fields for backend processing (temporarily disabled until backend deployment)
        // employees: this.convertHeadcountToInteger(formData.headcount || formData.company_profile?.headcount || '1-10'),
        // annual_revenue_sgd: this.convertRevenueToNumber(formData.revenue_range || formData.company_profile?.revenue_range || 'Under $1M'),
        // years_incorporated: 2025 - Math.min(parseInt(formData.incorporation_year || formData.company_profile?.incorporation_year || '2020'), 2024),
        // local_shareholding_pct: formData.localOwnershipPercentage || 100,
        // financially_viable: formData.financiallyViable !== undefined ? formData.financiallyViable : true,
        // business_stage: formData.businessStage || 'growth',
        
        // Legacy fields for backward compatibility
        industry: formData.industry || formData.company_profile?.industry || 'technology',
        company_size: this.determineCompanySize(formData.headcount || formData.company_profile?.headcount || '1-10'),
        project_type: formData.project_type || formData.projectDetails?.projectType || 'digitalization',
        grant_name: formData.grantType || 'EDGE',
        annual_revenue: this.convertRevenueToNumber(formData.revenue_range || formData.company_profile?.revenue_range || 'Under $1M'),
        employee_count: this.convertHeadcountToInteger(formData.headcount || formData.company_profile?.headcount || '1-10'),
        project_budget: formData.project_budget || formData.projectDetails?.projectBudget || 50000,
        project_duration_months: this.convertTimelineToMonths(formData.project_timeline || formData.projectDetails?.projectTimeline || '3-6 months')
      };

      debugLog('🔍 Detailed analysis request body:', eligibilityRequest);
      debugLog('🔍 Request body JSON:', JSON.stringify(eligibilityRequest, null, 2));

      // Call your backend's /eligibility/run endpoint
      const response = await this.deduplicatedRequest('/eligibility/run', {
        method: 'POST',
        body: JSON.stringify(eligibilityRequest),
      });

      debugLog('✅ Detailed analysis successful:', response);
      debugLog('✅ Response data:', response.data);
      
      // The /eligibility/run endpoint returns a different format
      // Transform it to match what the frontend expects
      const transformedResponse = this.transformEligibilityResponse(response.data, formData);
      
      return transformedResponse;
    } catch (error) {
      debugError('❌ Detailed analysis failed:', error);
      debugError('❌ Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        requestId: error.requestId
      });
      
      // Handle backend deployment issues gracefully
      if (error.message && error.message.includes('Enhanced Eligibility Assessment')) {
        debugWarn('⚠️ Backend deployment in progress, using fallback response');
        return {
          status: 'eligible',
          eligibility_status: 'eligible',
          confidence: 0.8,
          explanations: ['Backend is updating, showing preliminary results'],
          citations: [],
          data: {
            grant_name: formData.grantType || 'EDGE',
            eligibility_status: 'eligible',
            confidence_score: 0.8,
            risk_factors: ['Backend deployment in progress'],
            success_tips: ['Please try again in a few minutes for detailed analysis']
          }
        };
      }
      
      throw error;
    }
  }

  // NEW: Lockdown mode eligibility check with strict schema
  async checkEligibilityLockdown(formData) {
    debugLog('🔍 Checking eligibility with lockdown API:', formData);
    
    try {
      // Transform form data to match the strict lockdown schema
      const eligibilityRequest = {
        company_name: formData.companyName || 'Test Company',
        uen: formData.uen || '202012345A',
        industry: formData.industry || 'Technology',
        headcount: this.convertHeadcountToInteger(formData.headcount || '1-10'),
        revenue: this.convertRevenueToNumber(formData.revenue_range || 'Under $1M'),
        project_title: formData.projectTitle || formData.project_title || 'Digital Transformation Project',
        project_budget: formData.project_budget || 100000,
        grant_types: [formData.grantType || 'EDGE'],
        include_detailed_trace: false
      };

      debugLog('🔍 Lockdown eligibility request body:', eligibilityRequest);

      // Use the new lockdown API endpoint
      const response = await this.deduplicatedRequest('/eligibility/check', {
        method: 'POST',
        body: JSON.stringify(eligibilityRequest),
      });

      debugLog('✅ Lockdown eligibility check successful:', response);
      return response.data;
    } catch (error) {
      debugError('❌ Lockdown eligibility check failed:', error);
      debugError('❌ Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        requestId: error.requestId
      });
      throw error;
    }
  }

  // Transform the backend response to match frontend expectations
  transformEligibilityResponse(backendData, formData) {
    debugLog('🔄 Transforming backend response:', backendData);
    
    // The backend returns markdown, score, confidence, status, recommendations, etc.
    return {
      status: backendData.status || 'success',
      grant_name: formData.grantType || 'EDGE',
      company_name: formData.companyName || 'Test Company',
      overall_score: backendData.score || 0,
      eligibility_status: backendData.status === 'eligible' ? 'eligible' : 'not-eligible',
      confidence_level: backendData.confidence > 0.7 ? 'high' : backendData.confidence > 0.4 ? 'medium' : 'low',
      
      // Extract recommendations and next steps
      key_recommendations: backendData.recommendations?.map(rec => rec.title || rec.description || rec) || [
        'Complete all required company information',
        'Ensure project details are comprehensive',
        'Verify compliance requirements are met'
      ],
      next_steps: backendData.next_steps || [
        'Review and complete missing information',
        'Contact support for manual assessment',
        'Try again with more detailed data'
      ],
      
      // Risk factors based on missing requirements
      risk_factors: backendData.requirements_missing?.map(req => `Missing: ${req}`) || [
        'Incomplete company profile',
        'Limited project information'
      ],
      
      // Considerations
      considerations: [
        'Manual review may be required',
        'Additional documentation needed',
        'Consider professional consultation'
      ],
      
      // Additional metrics
      estimated_funding: this.calculateEstimatedFunding(backendData.score, formData.grantType),
      success_probability: backendData.confidence || 0.3,
      requirements_met: this.calculateRequirementsMet(backendData),
      requirements_total: 10,
      
      // Store original backend data
      backend_data: backendData,
      markdown: backendData.markdown,
      score: backendData.score,
      confidence: backendData.confidence,
      recommendations: backendData.recommendations || [],
      requirements_missing: backendData.requirements_missing || []
    };
  }

  // Calculate estimated funding based on score and grant type
  calculateEstimatedFunding(score, grantType) {
    if (!score || score < 60) return 0;
    
    const maxAmounts = {
      'psg': 30000,
      'edg': 100000,
      'mra': 50000
    };
    
    const maxAmount = maxAmounts[grantType?.toLowerCase()] || 30000;
    return Math.floor((score / 100) * maxAmount);
  }

  // Calculate requirements met based on backend response
  calculateRequirementsMet(backendData) {
    if (!backendData.requirements_missing) return 10;
    return Math.max(0, 10 - backendData.requirements_missing.length);
  }
  // Enhanced rules engine (v2)
  async evaluateRules(profile, grantType, extractionMetadata = {}, evalTrace = true) {
    debugLog('🔍 Evaluating rules with profile:', profile);
    
    try {
      const requestBody = {
        profile: profile,
        grantType: grantType,
        extractionMetadata: extractionMetadata,
        evalTrace: evalTrace
      };

      const response = await this.deduplicatedRequest('/v1/rules/v2/evaluate', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      
      debugLog('✅ Rules evaluation successful:', response);
      return response.data;
    } catch (error) {
      debugError('❌ Rules evaluation failed:', error);
      throw error;
    }
  }

  // Health check - using actual backend endpoint
  async healthCheck() {
    try {
      debugLog('🏥 Starting health check...');
      const response = await this.request('/health');
      debugLog('✅ Health check successful:', response);
      return { ok: true, data: response.data };
    } catch (error) {

      debugWarn('⚠️ Health check failed (likely CORS issue), but continuing...', error.message);
      // Return OK even if health check fails due to CORS - the actual endpoints work
      return { ok: true, data: { status: 'unknown', cors_issue: true } };
    }
  }

  // Simple ping function for connectivity testing
  async ping() {
    return this.healthCheck();
  }

  // Application Generator endpoints - using actual backend endpoint
  async generateApplication(projectData) {
    const parseBaselineEntries = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value !== 'string') return [];
      return value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const parts = line.split('|').map((p) => p.trim());
          return {
            processOrArea: parts[0] || '',
            currentVolume: parts[1] || '',
            currentTimeSpent: parts[2] || '',
            currentManpower: parts[3] || '',
            currentCost: parts[4] || '',
            currentErrorRate: parts[5] || '',
            currentToolOrSystem: parts[6] || '',
            operationalPain: parts[7] || '',
            evidenceSource: parts[8] || '',
            confidenceLevel: parts[9] || '',
          };
        });
    };
    const response = await this.request('/v1/application/generate', {
      method: 'POST',
      body: JSON.stringify({
        company_name: projectData.company_name || '',
        project_title: projectData.project_title || '',
        project_description: projectData.project_description || '',
        grant_type: projectData.grant_type || 'EDGE',
        project_budget: parseFloat(projectData.project_budget) || 0,
        requested_amount: parseFloat(projectData.requested_amount) || 0,
        cofunding_amount: projectData.cofunding_amount ?? null,
        project_timeline: projectData.project_timeline || '',
        technical_approach: projectData.technical_approach || '',
        expected_outcomes: projectData.expected_outcomes || '',
        risk_mitigation: projectData.risk_mitigation || '',
        contact_person: projectData.contact_person || '',
        contact_email: projectData.contact_email || '',
        company_registration: projectData.company_registration || '',
        project_objective: projectData.project_objective || '',
        problem_statement: projectData.problem_statement || '',
        target_beneficiaries: projectData.target_beneficiaries || '',
        current_state_baseline: projectData.current_state_baseline || '',
        baseline_entries: parseBaselineEntries(projectData.baseline_entries),
        proposed_solution: projectData.proposed_solution || '',
        implementation_plan_input: projectData.implementation_plan_input || '',
        measurement_method: projectData.measurement_method || '',
        budget_line_items: Array.isArray(projectData.budget_line_items) ? projectData.budget_line_items : [],
        applicant_contribution: parseFloat(projectData.applicant_contribution) || 0,
        psg_vendor_name: projectData.psg_vendor_name || '',
        psg_solution_package: projectData.psg_solution_package || '',
        psg_quotation_reference: projectData.psg_quotation_reference || '',
        psg_quotation_amount: projectData.psg_quotation_amount || '',
        psg_payment_or_contract_exists: Boolean(projectData.psg_payment_or_contract_exists),
        psg_manual_process_replaced: projectData.psg_manual_process_replaced || '',
        psg_staff_affected: projectData.psg_staff_affected || '',
        psg_current_processing_time: projectData.psg_current_processing_time || '',
        psg_current_transaction_volume: projectData.psg_current_transaction_volume || '',
        psg_expected_improvement: projectData.psg_expected_improvement || '',
        psg_implementation_evidence_reference: projectData.psg_implementation_evidence_reference || '',
        edg_capability_gap: projectData.edg_capability_gap || '',
        edg_transformation_objective: projectData.edg_transformation_objective || '',
        edg_consultant_vendor_scope: projectData.edg_consultant_vendor_scope || '',
        edg_business_impact_objective: projectData.edg_business_impact_objective || '',
        edg_strategic_competitiveness_impact: projectData.edg_strategic_competitiveness_impact || '',
        edg_not_bau_justification: projectData.edg_not_bau_justification || '',
        mra_target_overseas_market: projectData.mra_target_overseas_market || '',
        mra_overseas_activity_type: projectData.mra_overseas_activity_type || '',
        mra_partner_customer_channel: projectData.mra_partner_customer_channel || '',
        mra_market_entry_objective: projectData.mra_market_entry_objective || '',
        mra_expected_overseas_outcome: projectData.mra_expected_overseas_outcome || '',
        mra_market_evidence_source: projectData.mra_market_evidence_source || '',
        eeg_current_equipment_system: projectData.eeg_current_equipment_system || '',
        eeg_proposed_equipment_system: projectData.eeg_proposed_equipment_system || '',
        eeg_current_energy_usage: projectData.eeg_current_energy_usage || '',
        eeg_expected_savings: projectData.eeg_expected_savings || '',
        eeg_technical_spec_reference: projectData.eeg_technical_spec_reference || '',
        eeg_vendor_quotation_reference: projectData.eeg_vendor_quotation_reference || '',
      }),
    });
    return response;
  }

  // Document generation - using actual backend endpoint
  async generateDocument(profile, grant, templateType) {
    const response = await this.request('/v1/documents/generate', {
      method: 'POST',
      body: JSON.stringify({
        profile: profile,
        grant: grant,
        template_type: templateType,
        template_version: 'v1'
      }),
    });
    return response.data;
  }

  // Document review - robust endpoint resolution for reviewer only
  async reviewDocument(payload) {
    // Discover and cache the correct reviewer path for this deployment.
    // The correct endpoint is /review (not /api/review)
    const cacheKey = 'optra_reviewer_path';
    
    // Clear any invalid cached paths (like /api/review) immediately
    let cached = '';
    try {
      cached = localStorage.getItem(cacheKey) || '';
      // Clear invalid paths
      if (cached === '/api/review' || cached.includes('/api/')) {
        localStorage.removeItem(cacheKey);
        cached = '';
      }
    } catch (storageError) {
      debugWarn('Local storage unavailable while loading reviewer endpoint cache:', storageError);
      cached = '';
    }

    // Only try /review - it's the only valid endpoint
    const candidates = ['/review'];

    let lastError = null;
    for (const path of candidates) {
      try {
        // Skip OPTIONS preflight for review endpoint - it can be slow and POST will handle CORS
        // Go directly to POST request
        const response = await this.request(path, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        // Cache working path
        try { localStorage.setItem(cacheKey, path); } catch (storageError) { debugWarn('Unable to cache reviewer endpoint path:', storageError); }
        return response.data;
      } catch (err) {
        // On any error, clear cache and throw
        try { localStorage.removeItem(cacheKey); } catch (storageError) { debugWarn('Unable to clear reviewer endpoint cache:', storageError); }
        lastError = err;
        // No other candidates to try, so break
        break;
      }
    }
    // If failed, throw the error
    throw lastError || new Error('Reviewer endpoint /review failed');
  }

  // Email composition - using actual backend endpoint
  async composeEmail(profile, purpose, tone, recipientRole) {
    const response = await this.request('/v1/email/compose', {
      method: 'POST',
      body: JSON.stringify({
        profile: profile,
        purpose: purpose,
        tone: tone,
        recipient_role: recipientRole
      }),
    });
    return response.data;
  }

  // News feed - using actual backend endpoints
  async getNews(profile, topics) {
    const response = await this.request('/v1/newsfeed', {
      method: 'POST',
      body: JSON.stringify({
        profile: profile,
        topics: topics
      }),
    });
    return response.data;
  }

  // Comprehensive news with all sources
  async getComprehensiveNews(topics = ["EDGE", "SFEC", "ECI"], limit = 10) {
    const response = await this.request('/newsfeed/fetch', {
      method: 'POST',
      body: JSON.stringify({
        topics: topics,
        limit: limit,
        include_live: true,
        include_curated: true
      }),
    });
    return response.data;
  }

  // Live news only
  async getLiveNews(topics = ["EDGE", "SFEC", "ECI"], limit = 10) {
    const params = new URLSearchParams({
      topics: topics.join(','),
      limit: limit.toString()
    });
    const response = await this.request(`/newsfeed/live?${params}`);
    return response.data;
  }

  // Curated official content only
  async getCuratedNews(topics = ["EDGE", "SFEC", "ECI"], limit = 10) {
    const params = new URLSearchParams({
      topics: topics.join(','),
      limit: limit.toString()
    });
    const response = await this.request(`/newsfeed/curated?${params}`);
    return response.data;
  }

  // News summary
  async getNewsSummary() {
    const response = await this.request('/newsfeed/summary');
    return response.data;
  }

  // ===== USER HISTORY ENDPOINTS =====
  
  async getUserStats(email) {
    const response = await this.request(`/v1/user/stats?email=${encodeURIComponent(email)}`);
    return response;
  }

  async getUserActivity(email, limit = 10) {
    const response = await this.request(`/v1/user/activity?email=${encodeURIComponent(email)}&limit=${limit}`);
    return response;
  }

  async saveAssessment(email, grantType, companyName, score) {
    const response = await this.request(
      `/v1/user/save-assessment?email=${encodeURIComponent(email)}&grant_type=${encodeURIComponent(grantType)}&company_name=${encodeURIComponent(companyName)}&score=${score}`,
      { method: 'POST' }
    );
    return response;
  }

  async saveApplicationDraft(email, grantType, companyName, status = 'draft') {
    const response = await this.request(
      `/v1/user/save-draft?email=${encodeURIComponent(email)}&grant_type=${encodeURIComponent(grantType)}&company_name=${encodeURIComponent(companyName)}&status=${status}`,
      { method: 'POST' }
    );
    return response;
  }

  // ===== MANUAL GRANT SUBMISSIONS =====
  async listGrantSubmissions(ownerEmail, ownerUserId) {
    const response = await this.request(
      `/v1/user/grant-submissions?owner_email=${encodeURIComponent(ownerEmail)}&owner_user_id=${encodeURIComponent(ownerUserId)}`,
      { timeoutMs: 15000 }
    );
    return response?.data || { submissions: [] };
  }

  async createGrantSubmission(payload) {
    const response = await this.request('/v1/user/grant-submissions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || {};
  }

  async updateGrantSubmission(submissionId, payload) {
    const response = await this.request(`/v1/user/grant-submissions/${encodeURIComponent(submissionId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return response?.data || {};
  }

  async deleteGrantSubmission(submissionId, ownerEmail, ownerUserId) {
    const response = await this.request(
      `/v1/user/grant-submissions/${encodeURIComponent(submissionId)}?owner_email=${encodeURIComponent(ownerEmail)}&owner_user_id=${encodeURIComponent(ownerUserId)}`,
      { method: 'DELETE' }
    );
    return response?.data || {};
  }

  // ===== GRANT SIMULATOR =====
  getCachedGrantSimulatorForms() {
    const cached = this.grantSimulatorFormsCache;
    return cached && Date.now() - cached.fetchedAt < 10 * 60 * 1000 ? cached.data : null;
  }

  async getGrantSimulatorForms() {
    const cached = this.getCachedGrantSimulatorForms();
    if (cached) return cached;
    if (!this.grantSimulatorFormsPending) {
      this.grantSimulatorFormsPending = this.request('/v1/grant-simulator/forms')
        .then((response) => {
          const data = response?.data || { grants: [] };
          this.grantSimulatorFormsCache = { data, fetchedAt: Date.now() };
          return data;
        })
        .finally(() => { this.grantSimulatorFormsPending = null; });
    }
    return this.grantSimulatorFormsPending;
  }

  async optimizeGrantSimulatorAnswer(payload) {
    const response = await this.request('/v1/grant-simulator/optimize-answer', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || {};
  }

  async getGrantSimulatorChecklist(payload) {
    const response = await this.request('/v1/grant-simulator/checklist', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || { items: [] };
  }

  async saveGrantSimulator(payload) {
    const response = await this.request('/v1/grant-simulator/save', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || {};
  }

  // ===== DASHBOARD FOUNDATION ENDPOINTS =====
  async ensureDashboardWorkspace(ownerUserId, ownerEmail, workspaceName = 'Default Workspace') {
    if (this.dashboardApiUnsupported) {
      return null;
    }
    if (this.dashboardWorkspaceEnsureUnsupported) {
      const unsupportedError = new Error('Dashboard workspace ensure endpoint unavailable on current backend.');
      unsupportedError.name = 'MethodNotAllowedError';
      unsupportedError.meta = { status: 405, endpoint: '/v1/dashboard/workspaces/ensure' };
      throw unsupportedError;
    }
    try {
      const response = await this.deduplicatedRequest('/v1/dashboard/workspaces/ensure', {
        method: 'POST',
        body: JSON.stringify({
          owner_user_id: ownerUserId,
          owner_email: ownerEmail,
          workspace_name: workspaceName,
        }),
      });
      return response?.data || response || null;
    } catch (error) {
      const status = Number(error?.meta?.status || error?.cause?.meta?.status || 0);
      if (status === 405 || String(error?.name || '') === 'MethodNotAllowedError') {
        this.dashboardWorkspaceEnsureUnsupported = true;
        this.dashboardApiUnsupported = true;
        return null;
      }
      throw error;
    }
  }

  async ingestDashboardEvent(payload) {
    const response = await this.request('/v1/dashboard/events', {
      method: 'POST',
      body: JSON.stringify(payload),
      timeoutMs: 60000,
    });
    return response?.data || response || null;
  }

  async getDashboardSummary(workspaceId, { window = '30d' } = {}) {
    const params = new URLSearchParams({ workspace_id: workspaceId, window });
    const response = await this.request(`/v1/dashboard/summary?${params.toString()}`);
    return response?.data || response || null;
  }

  async listDashboardActions(workspaceId, options = {}) {
    const {
      status = null,
      priority = null,
      entityType = null,
      ownerUserId = null,
      slaState = null,
      limit = 25,
      cursor = null,
    } = options;
    const params = new URLSearchParams({ workspace_id: workspaceId, limit: String(limit) });
    if (status) params.set('status', status);
    if (priority) params.set('priority', priority);
    if (entityType) params.set('entity_type', entityType);
    if (ownerUserId) params.set('owner_user_id', ownerUserId);
    if (slaState) params.set('sla_state', slaState);
    if (cursor) params.set('cursor', cursor);
    const response = await this.deduplicatedRequest(`/v1/dashboard/actions?${params.toString()}`);
    return response?.data || response || { actions: [], next_cursor: null, has_more: false };
  }

  async listDashboardEvents(workspaceId, options = {}) {
    const {
      eventType = null,
      entityType = null,
      from = null,
      to = null,
      query = null,
      limit = 25,
      cursor = null,
    } = options;
    const params = new URLSearchParams({ workspace_id: workspaceId, limit: String(limit) });
    if (eventType) params.set('event_type', eventType);
    if (entityType) params.set('entity_type', entityType);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (query) params.set('query', query);
    if (cursor) params.set('cursor', cursor);
    const response = await this.request(`/v1/dashboard/events?${params.toString()}`);
    return response?.data || response || { events: [], next_cursor: null, has_more: false };
  }

  async createDashboardAction(payload) {
    const response = await this.request('/v1/dashboard/actions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || response || null;
  }

  async updateDashboardAction(actionId, payload) {
    const endpoint = `/v1/dashboard/actions/${encodeURIComponent(actionId)}`;
    const response = await this.deduplicatedRequest(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      timeoutMs: 60000,
    });
    return response?.data || response || null;
  }

  async bulkUpdateDashboardActions(payload) {
    const response = await this.request('/v1/dashboard/actions/bulk', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || response || { updated_count: 0, failed_ids: [], items: [] };
  }

  async listDashboardRoadmap(workspaceId) {
    if (this.dashboardApiUnsupported) return { items: [] };
    try {
      const response = await this.request(`/v1/dashboard/roadmap?workspace_id=${encodeURIComponent(workspaceId)}`);
      return response?.data || response || { items: [] };
    } catch (error) {
      const status = Number(error?.meta?.status || error?.cause?.meta?.status || 0);
      if (status === 405 || String(error?.name || '') === 'MethodNotAllowedError') {
        this.dashboardApiUnsupported = true;
        return { items: [] };
      }
      throw error;
    }
  }

  async upsertDashboardRoadmap(payload) {
    if (this.dashboardApiUnsupported) return null;
    try {
      const response = await this.request('/v1/dashboard/roadmap', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response?.data || response || null;
    } catch (error) {
      const status = Number(error?.meta?.status || error?.cause?.meta?.status || 0);
      if (status === 405 || String(error?.name || '') === 'MethodNotAllowedError') {
        this.dashboardApiUnsupported = true;
        return null;
      }
      throw error;
    }
  }

  async listPostApprovalWorkspaces({ ownerUserId = '', ownerEmail = '', limit = 50 } = {}) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (ownerUserId) params.set('owner_user_id', ownerUserId);
    if (ownerEmail) params.set('owner_email', ownerEmail);
    const response = await this.request(`/api/post-approval/workspaces?${params.toString()}`);
    return response?.data || response || { workspaces: [] };
  }

  async listDashboardFiles(workspaceId, options = {}) {
    if (this.dashboardApiUnsupported) return { files: [], next_cursor: null, has_more: false };
    const {
      folderId = null,
      tag = null,
      sourceTool = null,
      query = null,
      limit = 25,
      cursor = null,
    } = options;
    const params = new URLSearchParams({ workspace_id: workspaceId, limit: String(limit) });
    if (folderId) params.set('folder_id', folderId);
    if (tag) params.set('tag', tag);
    if (sourceTool) params.set('source_tool', sourceTool);
    if (query) params.set('query', query);
    if (cursor) params.set('cursor', cursor);
    try {
      const response = await this.deduplicatedRequest(`/v1/dashboard/files?${params.toString()}`);
      return response?.data || response || { files: [], next_cursor: null, has_more: false };
    } catch (error) {
      const status = Number(error?.meta?.status || error?.cause?.meta?.status || 0);
      if (status === 405 || String(error?.name || '') === 'MethodNotAllowedError') {
        this.dashboardApiUnsupported = true;
        return { files: [], next_cursor: null, has_more: false };
      }
      throw error;
    }
  }

  async listDashboardIntegrations(workspaceId) {
    const response = await this.request(`/v1/dashboard/integrations?workspace_id=${encodeURIComponent(workspaceId)}`);
    return response?.data || response || { integrations: [] };
  }

  async upsertDashboardIntegration(payload, integrationId = null) {
    const endpoint = integrationId
      ? `/v1/dashboard/integrations/${encodeURIComponent(integrationId)}`
      : '/v1/dashboard/integrations';
    const response = await this.request(endpoint, {
      method: integrationId ? 'PATCH' : 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || response || null;
  }

  async createDashboardFile(payload) {
    const response = await this.request('/v1/dashboard/files', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || response || null;
  }

  async updateDashboardFile(fileId, payload) {
    const response = await this.request(`/v1/dashboard/files/${encodeURIComponent(fileId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return response?.data || response || null;
  }

  async deleteDashboardFile(fileId, workspaceId) {
    const response = await this.request(
      `/v1/dashboard/files/${encodeURIComponent(fileId)}?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: 'DELETE' }
    );
    return response?.data || response || null;
  }

  async listDashboardFolders(workspaceId) {
    const response = await this.request(`/v1/dashboard/folders?workspace_id=${encodeURIComponent(workspaceId)}`);
    return response?.data || response || { folders: [] };
  }

  async upsertDashboardFolder(payload, folderId = null) {
    const endpoint = folderId
      ? `/v1/dashboard/folders/${encodeURIComponent(folderId)}`
      : '/v1/dashboard/folders';
    const response = await this.request(endpoint, {
      method: folderId ? 'PATCH' : 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || response || null;
  }

  async deleteDashboardFolder(folderId, workspaceId) {
    const response = await this.request(
      `/v1/dashboard/folders/${encodeURIComponent(folderId)}?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: 'DELETE' }
    );
    return response?.data || response || null;
  }

  async listDashboardTags(workspaceId) {
    const response = await this.request(`/v1/dashboard/tags?workspace_id=${encodeURIComponent(workspaceId)}`);
    return response?.data || response || { tags: [] };
  }

  async addDashboardTag(payload) {
    const response = await this.request('/v1/dashboard/tags', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || response || { tags: [] };
  }

  async getDashboardCompanyProfile(ownerUserId, ownerEmail = '') {
    const params = new URLSearchParams();
    params.set('owner_user_id', String(ownerUserId || '').trim());
    if (String(ownerEmail || '').trim()) {
      params.set('owner_email', String(ownerEmail || '').trim().toLowerCase());
    }
    const response = await this.request(`/v1/dashboard/company-profile?${params.toString()}`, {
      headers: this.getChatActorHeaders(),
    });
    return response?.data || response || { profile: null };
  }

  async upsertDashboardCompanyProfile(payload) {
    const response = await this.request('/v1/dashboard/company-profile', {
      method: 'POST',
      headers: this.getChatActorHeaders(),
      body: JSON.stringify(payload),
    });
    return response?.data || response || { profile: null };
  }

  async analyzeDashboardCompanyWebsite(payload) {
    try {
      const response = await this.request('/v1/dashboard/company-profile/analyze-website', {
        method: 'POST',
        headers: this.getChatActorHeaders(),
        body: JSON.stringify(payload),
      });
      return response?.data || response || null;
    } catch (error) {
      const status = Number(error?.status || error?.meta?.status || error?.meta?.lastError?.status || error?.cause?.meta?.status || 0);
      if (status !== 405) throw error;
      const fallback = await this.request('/v1/dashboard/analyze-company-website', {
        method: 'POST',
        headers: this.getChatActorHeaders(),
        body: JSON.stringify(payload),
      });
      return fallback?.data || fallback || null;
    }
  }

  async getAnalyticsSummary({ workspaceId = null, days = 30 } = {}) {
    const params = new URLSearchParams({ days: String(days) });
    if (workspaceId) params.set('workspace_id', workspaceId);
    const response = await this.request(`/v1/analytics/summary?${params.toString()}`);
    return response?.data || response || {};
  }

  async getAnalyticsFunnel({ workspaceId = null, days = 30 } = {}) {
    const params = new URLSearchParams({ days: String(days) });
    if (workspaceId) params.set('workspace_id', workspaceId);
    const response = await this.request(`/v1/analytics/funnel?${params.toString()}`);
    return response?.data || response || {};
  }

  // ===== DASHBOARD CHATS ENDPOINTS =====
  async ensureChatThread(payload) {
    const response = await this.request('/v1/dashboard/chats/threads/ensure', {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.ensure') },
      body: JSON.stringify(payload),
    });
    return response?.data || response || null;
  }

  async listChatThreads({ includeArchived = false } = {}) {
    const params = new URLSearchParams();
    if (includeArchived) params.set('include_archived', 'true');
    const response = await this.request(`/v1/dashboard/chats/threads${params.toString() ? `?${params.toString()}` : ''}`, {
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.list') },
    });
    return response?.data || response || [];
  }

  async getMyChatThread() {
    const response = await this.request('/v1/dashboard/chats/threads/me', {
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.me') },
    });
    return response?.data || response || null;
  }

  async listChatMessages(threadId, { cursor = null, limit = 50 } = {}) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', cursor);
    const response = await this.request(`/v1/dashboard/chats/threads/${encodeURIComponent(threadId)}/messages?${params.toString()}`, {
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('messages.list') },
    });
    return response?.data || response || { thread: null, messages: [], next_cursor: null, unread_count: 0, last_message_preview: null };
  }

  async sendChatMessage(threadId, body) {
    let messageBody = body;
    let attachmentIds = [];
    if (typeof body === 'object' && body !== null) {
      messageBody = body.body || '';
      attachmentIds = Array.isArray(body.attachment_ids) ? body.attachment_ids : [];
    }
    const response = await this.request(`/v1/dashboard/chats/threads/${encodeURIComponent(threadId)}/messages`, {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('messages.send') },
      body: JSON.stringify({ body: messageBody, attachment_ids: attachmentIds }),
    });
    return response?.data || response || null;
  }

  async markChatRead(threadId, lastReadMessageId = null) {
    chatUnreadCountCache = null;
    const response = await this.request(`/v1/dashboard/chats/threads/${encodeURIComponent(threadId)}/read`, {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.read') },
      body: JSON.stringify({ last_read_message_id: lastReadMessageId }),
    });
    return response?.data || response || { success: false };
  }

  /**
   * Unread total for the badge. The sidebar, the Messages page and local chat events all ask for
   * it; one request is shared while in flight and its answer reused for five seconds, since the
   * backend counts it with three Supabase queries (about a second on a cold connection).
   */
  async getChatUnreadCount({ fresh = false } = {}) {
    const headers = { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.unread_count') };
    const key = JSON.stringify(this.getChatActorHeaders() || {});
    const now = Date.now();
    const cached = chatUnreadCountCache;
    if (!fresh && cached && cached.key === key) {
      if (cached.promise) return cached.promise;
      if (cached.value && now - cached.at < CHAT_UNREAD_COUNT_TTL_MS) return cached.value;
    }
    const promise = this.request('/v1/dashboard/chats/unread-count', { headers })
      .then((response) => {
        const value = response?.data || response || { unread_count: 0 };
        chatUnreadCountCache = { key, value, at: Date.now(), promise: null };
        return value;
      })
      .catch((error) => {
        if (chatUnreadCountCache?.promise === promise) chatUnreadCountCache = null;
        throw error;
      });
    chatUnreadCountCache = { key, value: null, at: now, promise };
    return promise;
  }

  async pinChatThread(threadId, value = true) {
    return this.request(`/v1/dashboard/chats/threads/${encodeURIComponent(threadId)}/pin`, {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.pin') },
      body: JSON.stringify({ value: Boolean(value) }),
    });
  }

  async archiveChatThread(threadId) {
    return this.request(`/v1/dashboard/chats/threads/${encodeURIComponent(threadId)}/archive`, {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.archive') },
    });
  }

  async reopenChatThread(threadId) {
    return this.request(`/v1/dashboard/chats/threads/${encodeURIComponent(threadId)}/reopen`, {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.reopen') },
    });
  }

  async markChatThreadUnread(threadId) {
    return this.request(`/v1/dashboard/chats/threads/${encodeURIComponent(threadId)}/mark-unread`, {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.mark_unread') },
    });
  }

  async getChatThreadContext(threadId) {
    return this.request(`/v1/dashboard/chats/threads/${encodeURIComponent(threadId)}/context`, {
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.context') },
    });
  }

  async searchChats({ q = '', client = '', hasAttachment = false, unread = false } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (client) params.set('client', client);
    if (hasAttachment) params.set('has_attachment', 'true');
    if (unread) params.set('unread', 'true');
    return this.request(`/v1/dashboard/chats/search?${params.toString()}`, {
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('threads.search') },
    });
  }

  async createChatAttachmentPresign(payload) {
    return this.request('/v1/dashboard/chats/attachments/presign', {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('attachments.presign') },
      body: JSON.stringify(payload),
    });
  }

  async commitChatAttachment(attachmentId, dataUrl = '') {
    return this.request('/v1/dashboard/chats/attachments/commit', {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('attachments.commit') },
      body: JSON.stringify({ attachment_id: attachmentId, data_url: dataUrl || undefined }),
    });
  }

  async createChatMessageAction(messageId, payload) {
    return this.request(`/v1/dashboard/chats/messages/${encodeURIComponent(messageId)}/actions`, {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('messages.action') },
      body: JSON.stringify(payload),
    });
  }

  async getChatDevSnapshot() {
    return this.request('/v1/dashboard/chats/dev/snapshot', {
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('dev.snapshot') },
    });
  }

  async resetLocalhostChats() {
    return this.request('/v1/dashboard/chats/dev/reset-localhost', {
      method: 'POST',
      headers: { ...this.getChatActorHeaders(), ...this.getChatDebugHeaders('dev.reset_localhost') },
    });
  }

  // ===== CLIENT OPS ENDPOINTS =====
  /** Forget the cached client list (a refresh timer or a focus event wants the current rows). */
  invalidateDashboardClients() {
    this.listDashboardClientsCache.clear();
  }

  async listDashboardClients(limit = 200, { fresh = false } = {}) {
    const normalizedLimit = Number(limit) || 200;
    const cacheKey = `limit:${normalizedLimit}`;
    const cached = this.listDashboardClientsCache.get(cacheKey);
    if (!fresh && cached && Date.now() - Number(cached.fetchedAt || 0) < 30000) {
      return cached.payload;
    }
    const response = await this.deduplicatedRequest(`/v1/dashboard/clients?limit=${normalizedLimit}`, {
      timeoutMs: IS_DEV ? 12000 : undefined,
      headers: this.getChatActorHeaders(),
    });
    const payload = response?.data || response || { clients: [] };
    this.listDashboardClientsCache.set(cacheKey, {
      fetchedAt: Date.now(),
      payload,
    });
    return payload;
  }

  async getDashboardClient(clientId) {
    const response = await this.request(`/v1/dashboard/clients/${encodeURIComponent(String(clientId || ''))}`, {
      headers: this.getChatActorHeaders(),
    });
    return response?.data || response || { client: null };
  }

  /**
   * Admin: one client's account, ops record (assessment state, selected grants, dashboard access)
   * and every assessment run, newest first. GET /v1/dashboard/clients/{id}/overview.
   */
  async getClientOverview(clientAccountId) {
    const response = await this.request(
      `/v1/dashboard/clients/${encodeURIComponent(String(clientAccountId || ''))}/overview`,
      { headers: this.getChatActorHeaders() },
    );
    return response?.data || response || null;
  }

  async getClientOpsRecord({ clientId = '', clientEmail = '' } = {}) {
    const params = new URLSearchParams();
    if (String(clientId || '').trim()) params.set('client_id', String(clientId || '').trim());
    if (String(clientEmail || '').trim()) params.set('client', String(clientEmail || '').trim().toLowerCase());
    const response = await this.request(`/v1/dashboard/client-ops/record?${params.toString()}`, {
      headers: this.getChatActorHeaders(),
    });
    return response?.data || response || { record: null };
  }

  /**
   * The signed-in client's ops record. Several dashboard surfaces (the gate, the sidebar, the
   * home page, the assessment page) ask for it on the same navigation, so one request is shared
   * while it is in flight and its answer is reused for a few seconds; a write invalidates it.
   */
  async getMyClientOpsRecord({ fresh = false } = {}) {
    const headers = this.getChatActorHeaders();
    const key = JSON.stringify(headers || {});
    const now = Date.now();
    const cached = myClientOpsRecordCache;
    if (!fresh && cached && cached.key === key) {
      if (cached.promise) return cached.promise;
      if (cached.value && now - cached.at < MY_CLIENT_OPS_RECORD_TTL_MS) return cached.value;
    }
    const promise = this.request('/v1/dashboard/client-ops/record/me', { headers })
      .then((response) => {
        const value = response?.data || response || { record: null };
        myClientOpsRecordCache = { key, value, at: Date.now(), promise: null };
        return value;
      })
      .catch((error) => {
        if (myClientOpsRecordCache?.promise === promise) myClientOpsRecordCache = null;
        throw error;
      });
    myClientOpsRecordCache = { key, value: null, at: now, promise };
    return promise;
  }

  /** Forget the cached ops record (after a write, or when a fresh read matters). */
  invalidateMyClientOpsRecord() {
    myClientOpsRecordCache = null;
  }

  async updateClientOpsRecord(payload) {
    myClientOpsRecordCache = null;
    const response = await this.request('/v1/dashboard/client-ops/record', {
      method: 'PUT',
      headers: {
        ...this.getChatActorHeaders(),
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    return response?.data || response || { record: null };
  }

  async publishClientOpsRecord({ clientId = '', clientEmail = '', idempotencyKey = '', trigger = 'manual_publish' } = {}) {
    const response = await this.request('/v1/dashboard/client-ops/publish', {
      method: 'POST',
      headers: this.getChatActorHeaders(),
      body: JSON.stringify({
        ...(String(clientId || '').trim() ? { client_id: String(clientId || '').trim() } : {}),
        ...(String(clientEmail || '').trim() ? { client_email: String(clientEmail || '').trim().toLowerCase() } : {}),
        ...(String(idempotencyKey || '').trim() ? { idempotency_key: String(idempotencyKey || '').trim() } : {}),
        ...(String(trigger || '').trim() ? { trigger: String(trigger || '').trim() } : {}),
      }),
    });
    return response?.data || response || { record: null };
  }

  async grantClientOpsPostApprovalAccess({ clientId = '', clientEmail = '', trigger = 'manual_grant' } = {}) {
    const payload = JSON.stringify({
      ...(String(clientId || '').trim() ? { client_id: String(clientId || '').trim() } : {}),
      ...(String(clientEmail || '').trim() ? { client_email: String(clientEmail || '').trim().toLowerCase() } : {}),
      ...(String(trigger || '').trim() ? { trigger: String(trigger || '').trim() } : {}),
    });
    try {
      const response = await this.request('/v1/dashboard/client-ops/post-approval-access', {
        method: 'POST',
        headers: this.getChatActorHeaders(),
        body: payload,
      });
      return response?.data || response || { record: null };
    } catch (error) {
      const status = Number(error?.meta?.status || error?.meta?.lastError?.status || error?.cause?.meta?.status || 0);
      if (status !== 405 && String(error?.name || "") !== 'MethodNotAllowedError') {
        throw error;
      }
      const response = await this.request('/v1/dashboard/client-ops/post-approval-access', {
        method: 'PUT',
        headers: this.getChatActorHeaders(),
        body: payload,
      });
      return response?.data || response || { record: null };
    }
  }

  async revokeClientOpsPostApprovalAccess({ clientId = '', clientEmail = '', trigger = 'manual_revoke' } = {}) {
    const payload = JSON.stringify({
      ...(String(clientId || '').trim() ? { client_id: String(clientId || '').trim() } : {}),
      ...(String(clientEmail || '').trim() ? { client_email: String(clientEmail || '').trim().toLowerCase() } : {}),
      ...(String(trigger || '').trim() ? { trigger: String(trigger || '').trim() } : {}),
    });
    const response = await this.request('/v1/dashboard/client-ops/post-approval-access/revoke', {
      method: 'POST',
      headers: this.getChatActorHeaders(),
      body: payload,
    });
    return response?.data || response || { record: null };
  }

  async grantClientOpsDashboardAccess({ clientId = '', clientEmail = '', trigger = 'manual_grant' } = {}) {
    const payload = JSON.stringify({
      ...(String(clientId || '').trim() ? { client_id: String(clientId || '').trim() } : {}),
      ...(String(clientEmail || '').trim() ? { client_email: String(clientEmail || '').trim().toLowerCase() } : {}),
      ...(String(trigger || '').trim() ? { trigger: String(trigger || '').trim() } : {}),
    });
    try {
      const response = await this.request('/v1/dashboard/client-ops/dashboard-access', {
        method: 'POST',
        headers: this.getChatActorHeaders(),
        body: payload,
      });
      return response?.data || response || { record: null };
    } catch (error) {
      const status = Number(error?.meta?.status || error?.meta?.lastError?.status || error?.cause?.meta?.status || 0);
      if (status !== 405 && String(error?.name || "") !== 'MethodNotAllowedError') {
        throw error;
      }
      const response = await this.request('/v1/dashboard/client-ops/dashboard-access', {
        method: 'PUT',
        headers: this.getChatActorHeaders(),
        body: payload,
      });
      return response?.data || response || { record: null };
    }
  }

  async revokeClientOpsDashboardAccess({ clientId = '', clientEmail = '', trigger = 'manual_revoke' } = {}) {
    const response = await this.request('/v1/dashboard/client-ops/dashboard-access/revoke', {
      method: 'POST',
      headers: this.getChatActorHeaders(),
      body: JSON.stringify({
        ...(String(clientId || '').trim() ? { client_id: String(clientId || '').trim() } : {}),
        ...(String(clientEmail || '').trim() ? { client_email: String(clientEmail || '').trim().toLowerCase() } : {}),
        ...(String(trigger || '').trim() ? { trigger: String(trigger || '').trim() } : {}),
      }),
    });
    return response?.data || response || { record: null };
  }

  async updateSelectedGrants(grants = [], { clientId = '', clientEmail = '' } = {}) {
    const response = await this.request('/v1/dashboard/client-ops/selected-grants', {
      method: 'PUT',
      headers: {
        ...this.getChatActorHeaders(),
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grants: Array.isArray(grants) ? grants : [],
        ...(String(clientId || '').trim() ? { client_id: String(clientId || '').trim() } : {}),
        ...(String(clientEmail || '').trim() ? { client_email: String(clientEmail || '').trim().toLowerCase() } : {}),
      }),
    });
    return response?.data || response || { record: null };
  }

  async resetClientAssessment({ clientId = '', clientEmail = '', trigger = 'manual_reset' } = {}) {
    const response = await this.request('/v1/dashboard/client-ops/assessment/reset', {
      method: 'POST',
      headers: this.getChatActorHeaders(),
      body: JSON.stringify({
        ...(String(clientId || '').trim() ? { client_id: String(clientId || '').trim() } : {}),
        ...(String(clientEmail || '').trim() ? { client_email: String(clientEmail || '').trim().toLowerCase() } : {}),
        ...(String(trigger || '').trim() ? { trigger: String(trigger || '').trim() } : {}),
      }),
    });
    return response?.data || response || { record: null };
  }

  async getClientState({ clientId = '', clientEmail = '' } = {}) {
    const params = new URLSearchParams();
    if (String(clientId || '').trim()) params.set('client_id', String(clientId || '').trim());
    if (String(clientEmail || '').trim()) params.set('client_email', String(clientEmail || '').trim().toLowerCase());
    const response = await this.request(`/v1/dashboard/client-state${params.toString() ? `?${params.toString()}` : ''}`, {
      headers: this.getChatActorHeaders(),
    });
    return response?.data || response || null;
  }

  async resetLocalDashboardState() {
    const response = await this.request('/v1/dashboard/dev/reset-dashboard-localhost', {
      method: 'POST',
      headers: this.getChatActorHeaders(),
      body: JSON.stringify({}),
    });
    return response?.data || response || { ok: false };
  }

  async listClientOpsEvents({ clientId = '', clientEmail = '', limit = 30 } = {}) {
    const params = new URLSearchParams({
      limit: String(limit),
    });
    if (String(clientId || '').trim()) params.set('client_id', String(clientId || '').trim());
    if (String(clientEmail || '').trim()) params.set('client', String(clientEmail || '').trim().toLowerCase());
    const response = await this.request(`/v1/dashboard/client-ops/events?${params.toString()}`, {
      headers: this.getChatActorHeaders(),
    });
    return response?.data || response || { events: [] };
  }

  async recomputeClientOps({ clientId = '', clientEmail = '', idempotencyKey = '', trigger = 'manual_recompute' } = {}) {
    const response = await this.request('/v1/dashboard/client-ops/recompute', {
      method: 'POST',
      headers: this.getChatActorHeaders(),
      body: JSON.stringify({
        ...(String(clientId || '').trim() ? { client_id: String(clientId || '').trim() } : {}),
        ...(String(clientEmail || '').trim() ? { client_email: String(clientEmail || '').trim().toLowerCase() } : {}),
        ...(String(idempotencyKey || '').trim() ? { idempotency_key: String(idempotencyKey || '').trim() } : {}),
        ...(String(trigger || '').trim() ? { trigger: String(trigger || '').trim() } : {}),
      }),
    });
    return response?.data || response || { record: null };
  }

  async autoSyncClientOps({
    clientId = '',
    clientEmail = '',
    featureKey = '',
    resultPayload = {},
    generatedFiles = [],
    shareLinks = [],
    trigger = 'feature_completed',
    idempotencyKey = '',
  } = {}) {
    const response = await this.request('/v1/dashboard/client-ops/auto-sync', {
      method: 'POST',
      headers: this.getChatActorHeaders(),
      body: JSON.stringify({
        ...(String(clientId || '').trim() ? { client_id: String(clientId || '').trim() } : {}),
        ...(String(clientEmail || '').trim() ? { client_email: String(clientEmail || '').trim().toLowerCase() } : {}),
        feature_key: String(featureKey || '').trim().toLowerCase(),
        result_payload: (resultPayload && typeof resultPayload === 'object') ? resultPayload : {},
        generated_files: Array.isArray(generatedFiles) ? generatedFiles : [],
        share_links: Array.isArray(shareLinks) ? shareLinks : [],
        ...(String(trigger || '').trim() ? { trigger: String(trigger || '').trim() } : {}),
        ...(String(idempotencyKey || '').trim() ? { idempotency_key: String(idempotencyKey || '').trim() } : {}),
      }),
    });
    return response?.data || response || { record: null };
  }

  async uploadClientOpsEvidence(payload) {
    const sessionEmail = getSessionEmail();
    const response = await this.request('/v1/dashboard/client-ops/evidence', {
      method: 'POST',
      headers: {
        ...(sessionEmail ? { 'X-User-Email': sessionEmail } : {}),
        ...((typeof window !== 'undefined' && (window.Clerk?.user?.id || window.Clerk?.session?.user?.id)) ? { 'X-Clerk-User-Id': (window.Clerk?.user?.id || window.Clerk?.session?.user?.id || '') } : {}),
      },
      body: JSON.stringify(payload),
    });
    return response?.data || response || { record: null };
  }

  async uploadDashboardDocument({ file, clientId = '', clientEmail = '', folderId = '', checklistItemId = '' } = {}) {
    const form = new FormData();
    form.append('file', file);
    if (String(clientId || '').trim()) form.append('client_id', String(clientId || '').trim());
    if (String(clientEmail || '').trim()) form.append('client_email', String(clientEmail || '').trim().toLowerCase());
    if (String(folderId || '').trim()) form.append('folder_id', String(folderId || '').trim());
    if (String(checklistItemId || '').trim()) form.append('checklist_item_id', String(checklistItemId || '').trim());
    const response = await this.request('/v1/dashboard/documents/upload', {
      method: 'POST',
      headers: this.getChatActorHeaders(),
      body: form,
      timeoutMs: 45000,
    });
    return response?.data || response || null;
  }

  // CORS test endpoint
  async testCors() {
    return this.request('/cors-test');
  }

  // Vercel integration test
  async testVercelIntegration() {
    return this.request('/vercel-test');
  }

  // Main orchestration endpoint
  async orchestrate(profile, query) {
    return this.request('/v1/orchestrate', {
      method: 'POST',
      body: JSON.stringify({
        profile: profile,
        query: query
      }),
    });
  }

  // PDF generation for eligibility results
  async generateEligibilityPDF(resultsData) {
    return this.request('/v1/eligibility/pdf', {
      method: 'POST',
      body: JSON.stringify(resultsData),
    });
  }

  // Share eligibility results
  async shareEligibilityResults(resultsData, shareMethod = 'email') {
    return this.request('/v1/eligibility/share', {
      method: 'POST',
      body: JSON.stringify({
        results: resultsData,
        share_method: shareMethod
      }),
    });
  }

  // Test network connectivity
  async testConnectivity() {
    const backendUrls = this.getBackendUrls();
    let lastError = null;
    
    debugLog('🔍 Testing connectivity to backend URLs:', backendUrls);
    
    for (const baseUrl of backendUrls) {
      try {
        debugLog(`🔄 Testing ${baseUrl}/health...`);
      const startTime = Date.now();
        const response = await fetch(`${baseUrl}/health`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const endTime = Date.now();
      const responseTime = endTime - startTime;
        
        debugLog(`✅ ${baseUrl}/health responded with status: ${response.status}`);
      
      if (response.ok) {
        return {
          success: true,
          responseTime: `${responseTime}ms`,
          status: response.status,
          statusText: response.statusText,
            backendUrl: baseUrl,
          };
        } else {
          debugLog(`⚠️ ${baseUrl}/health returned status: ${response.status} ${response.statusText}`);
          lastError = {
            success: false,
            status: response.status,
            statusText: response.statusText,
            error: `HTTP ${response.status}: ${response.statusText}`,
            backendUrl: baseUrl,
          };
        }
      } catch (error) {
        debugLog(`❌ ${baseUrl}/health failed:`, error.message);
        lastError = {
          success: false,
          error: error.message,
          type: error.name,
          backendUrl: baseUrl,
        };
      }
    }
    
    // If we get here, all backends failed
    debugLog('💥 All backend servers failed to connect');
    return {
      success: false,
      error: 'All backend servers are unreachable',
      type: 'ConnectionError',
      triedUrls: backendUrls,
      lastError: lastError,
    };
  }

  // Simple test method for debugging
  async simpleTest() {
    debugLog('🧪 Running simple connectivity test...');
    debugLog('📍 Current hostname:', window.location.hostname);
    debugLog('Environment:', import.meta?.env?.MODE || 'development');
    debugLog('🔗 Backend URLs to try:', this.getBackendUrls());
    
    try {
      const result = await this.testConnectivity();
      debugLog('📊 Connectivity test result:', result);
      return result;
    } catch (error) {

      debugError('💥 Connectivity test failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Test eligibility endpoint specifically
  async testEligibilityEndpoint() {
    debugLog('🧪 Testing eligibility endpoint specifically...');
    
    const mockFormData = {
      industry: 'Technology',
      headcount: 50,
      revenue_range: '1M-10M',
      incorporation_year: 2020,
      singapore_based: true,
      project_type: 'Digital Transformation',
      project_budget: 100000,
      project_timeline: '6-12 months',
      cofunding_capacity: 30,
      available_documents: ['ACRA Business Profile'],
      compliance_declarations: {
        no_outstanding_issues: true,
        meets_local_shareholding: true,
        authorized_to_apply: true
      }
    };
    
    try {
      debugLog('📤 Sending mock form data to /v1/eligibility...');
      const result = await this.request('/v1/eligibility', {
        method: 'POST',
        body: JSON.stringify(mockFormData)
      });
      
      debugLog('✅ Eligibility endpoint test successful:', result);
      return {
        success: true,
        message: 'Eligibility endpoint is working correctly',
        data: result
      };
    } catch (error) {

      debugError('💥 Eligibility endpoint test failed:', error);
      return {
        success: false,
        error: error.message,
        requestId: error.requestId,
        triedUrls: error.triedUrls,
        lastError: error.lastError
      };
    }
  }

  // Simple test to check if endpoint exists
  async testEndpointExists(endpoint) {
    debugLog(`🔍 Testing if endpoint ${endpoint} exists...`);
    
    try {
      // Try a simple GET request first to see if the endpoint exists
      const result = await this.request(endpoint, { method: 'GET' });
      return {
        success: true,
        message: `Endpoint ${endpoint} exists and responds to GET`,
        data: result
      };
    } catch (error) {
      debugLog(`❌ GET request to ${endpoint} failed:`, error.message);
      
      // If GET fails, try OPTIONS to check if endpoint exists
      try {
        const optionsResult = await fetch(`${this.baseURL}${endpoint}`, {
          method: 'OPTIONS',
          mode: 'cors'
        });
        
        return {
          success: true,
          message: `Endpoint ${endpoint} exists (OPTIONS successful)`,
          status: optionsResult.status,
          headers: Object.fromEntries(optionsResult.headers.entries())
        };
      } catch (optionsError) {
        return {
          success: false,
          error: `Endpoint ${endpoint} does not exist or is not accessible`,
          getError: error.message,
          optionsError: optionsError.message
        };
      }
    }
  }

  // Discover what endpoints exist on the backend
  async discoverEndpoints() {
    debugLog('🔍 Discovering available endpoints...');
    
    const commonEndpoints = [
      '/health',
      '/v1/eligibility',
      '/eligibility',
      '/api/eligibility',
      '/v1/api/eligibility',
      '/grant/eligibility',
      '/v1/grant/eligibility',
      '/api/v1/eligibility',
      '/',
      '/docs',
      '/openapi.json',
      '/swagger',
      '/redoc'
    ];
    
    const results = {};
    
    for (const endpoint of commonEndpoints) {
      try {
        debugLog(`🔍 Testing ${endpoint}...`);
        const result = await this.request(endpoint, { method: 'GET' });
        results[endpoint] = {
          exists: true,
          method: 'GET',
          status: 'success',
          data: result
        };
      } catch (error) {
        // Try POST for endpoints that might only accept POST
        if (endpoint.includes('eligibility')) {
          try {
            const mockData = { test: true };
            const result = await this.request(endpoint, { 
              method: 'POST',
              body: JSON.stringify(mockData)
            });
            results[endpoint] = {
              exists: true,
              method: 'POST',
              status: 'success',
              data: result
            };
          } catch (postError) {
            results[endpoint] = {
              exists: false,
              method: 'POST',
              error: postError.message
            };
          }
        } else {
          results[endpoint] = {
            exists: false,
            method: 'GET',
            error: error.message
          };
        }
      }
    }
    
    return {
      success: true,
      message: 'Endpoint discovery complete',
      results: results,
      summary: {
        total: commonEndpoints.length,
        exists: Object.values(results).filter(r => r.exists).length,
        notFound: Object.values(results).filter(r => !r.exists).length
      }
    };
  }

  // Raw connectivity test - bypasses our complex logic
  async testRawConnectivity() {
    debugLog('🔌 Testing raw connectivity...');
    
    const testUrls = [
      'http://localhost:8000',
      'http://localhost:3000', 
      'http://localhost:5000',
      'http://localhost:8080',
      'https://optra-backend.onrender.com'
    ];
    
    const results = {};
    
    for (const baseUrl of testUrls) {
      try {
        debugLog(`🔌 Testing raw connection to ${baseUrl}...`);
        
        // Simple fetch without CORS mode
        const response = await fetch(`${baseUrl}/health`, {
          method: 'GET',
          mode: 'no-cors' // This bypasses CORS restrictions
        });
        
        results[baseUrl] = {
          reachable: true,
          status: response.status,
          statusText: response.statusText,
          type: response.type,
          cors: 'no-cors'
        };
        
        debugLog(`✅ ${baseUrl} is reachable (no-cors mode)`);
        
      } catch (error) {
        debugLog(`❌ ${baseUrl} failed:`, error.message);
        results[baseUrl] = {
          reachable: false,
          error: error.message,
          type: error.name
        };
      }
    }
    
    return {
      success: true,
      message: 'Raw connectivity test complete',
      results: results,
      summary: {
        total: testUrls.length,
        reachable: Object.values(results).filter(r => r.reachable).length,
        unreachable: Object.values(results).filter(r => !r.reachable).length
      }
    };
  }

  // Test what endpoints exist specifically on port 5000
  async testPort5000Endpoints() {
    debugLog('🔍 Testing endpoints specifically on port 5000...');
    
    const baseUrl = 'http://localhost:5000';
    const testEndpoints = [
      '/health',
      '/',
      '/api',
      '/v1',
      '/eligibility',
      '/v1/eligibility',
      '/grant',
      '/docs',
      '/openapi.json'
    ];
    
    const results = {};
    
    for (const endpoint of testEndpoints) {
      try {
        debugLog(`🔍 Testing ${baseUrl}${endpoint}...`);
        
        // Try GET first
        try {
          const response = await fetch(`${baseUrl}${endpoint}`, {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          results[endpoint] = {
            method: 'GET',
            status: response.status,
            statusText: response.statusText,
            cors: 'cors',
            success: response.ok
          };
          
          if (response.ok) {
            try {
              const data = await response.json();
              results[endpoint].data = data;
            } catch (error) {
              results[endpoint].data = 'Not JSON';
              debugWarn(`Endpoint ${endpoint} did not return JSON:`, error);
            }
          }
          
        } catch (getError) {
          debugWarn(`GET request failed for ${endpoint}:`, getError);
          // If GET fails, try POST for endpoints that might only accept POST
          if (endpoint.includes('eligibility')) {
            try {
              const mockData = { test: true };
              const response = await fetch(`${baseUrl}${endpoint}`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(mockData)
              });
              
              results[endpoint] = {
                method: 'POST',
                status: response.status,
                statusText: response.statusText,
                cors: 'cors',
                success: response.ok
              };
              
            } catch (postError) {
              results[endpoint] = {
                method: 'POST',
                error: postError.message,
                cors: 'cors'
              };
              debugWarn(`POST request failed for ${endpoint}:`, postError);
            }
          } else {
            results[endpoint] = {
              method: 'GET',
              error: getError.message,
              cors: 'cors'
            };
          }
        }
        
      } catch (error) {
        results[endpoint] = {
          error: error.message,
          cors: 'cors'
        };
      }
    }
    
    return {
      success: true,
      message: 'Port 5000 endpoint test complete',
      baseUrl: baseUrl,
      results: results,
      summary: {
        total: testEndpoints.length,
        working: Object.values(results).filter(r => r.success === true).length,
        failing: Object.values(results).filter(r => r.success === false).length,
        errors: Object.values(results).filter(r => r.error).length
      }
    };
  }

  // More accurate server test - checks if server actually responds
  async testServerActuallyRunning() {
    debugLog('🔍 Testing if servers are actually running and responding...');
    
    const testUrls = [
      'http://localhost:8000',
      'http://localhost:3000',
      'http://localhost:5000',
      'http://localhost:8080',
      'https://optra-backend.onrender.com'
    ];
    
    const results = {};
    
    for (const baseUrl of testUrls) {
      try {
        debugLog(`🔍 Testing if server is actually running on ${baseUrl}...`);
        
        // Set a timeout to detect if server is not responding
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        try {
          const response = await fetch(`${baseUrl}/health`, {
            method: 'GET',
            mode: 'cors',
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          results[baseUrl] = {
            serverRunning: true,
            status: response.status,
            statusText: response.statusText,
            responseTime: 'immediate',
            cors: 'cors'
          };
          
          debugLog(`✅ ${baseUrl} has a server running and responding`);
          
        } catch (fetchError) {
          clearTimeout(timeoutId);
          
          if (fetchError.name === 'AbortError') {
            results[baseUrl] = {
              serverRunning: false,
              error: 'Request timed out (server not responding)',
              responseTime: 'timeout',
              cors: 'cors'
            };
          } else {
            results[baseUrl] = {
              serverRunning: false,
              error: fetchError.message,
              responseTime: 'immediate_failure',
              cors: 'cors'
            };
          }
          
          debugLog(`❌ ${baseUrl} server not running or not responding`);
        }
        
      } catch (error) {
        results[baseUrl] = {
          serverRunning: false,
          error: error.message,
          responseTime: 'error',
          cors: 'cors'
        };
      }
    }
    
    return {
      success: true,
      message: 'Server responsiveness test complete',
      results: results,
      summary: {
        total: testUrls.length,
        running: Object.values(results).filter(r => r.serverRunning).length,
        notRunning: Object.values(results).filter(r => !r.serverRunning).length
      }
    };
  }

  // Simple test to see if production backend is reachable
  async testProductionBackendSimple() {
    debugLog('🧪 Testing production backend with simple fetch...');
    
    try {
      // Test with a simple GET request to /health
      debugLog('📤 Testing GET /health on production backend...');
      const response = await fetch('https://optra-backend.onrender.com/health', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      debugLog('📥 Response received:', response);
      debugLog('   Status:', response.status, response.statusText);
      debugLog('   Headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const data = await response.json();
        debugLog('✅ Production backend is reachable:', data);
        return {
          success: true,
          message: 'Production backend is reachable',
          status: response.status,
          data: data
        };
      } else {
        debugLog('❌ Production backend responded with error:', response.status);
        return {
          success: false,
          message: `Production backend error: ${response.status}`,
          status: response.status
        };
      }
      
    } catch (error) {

      debugError('💥 Production backend test failed:', error);
      return {
        success: false,
        message: 'Production backend test failed',
        error: error.message,
        type: error.name
      };
    }
  }

  // Test CORS policy on production backend
  async testCorsPolicy() {
    debugLog('🧪 Testing CORS policy on production backend...');
    
    try {
      // Test OPTIONS request (preflight)
      debugLog('📤 Testing OPTIONS request to /v1/eligibility...');
      const optionsResponse = await fetch('https://optra-backend.onrender.com/v1/eligibility', {
        method: 'OPTIONS',
        mode: 'cors',
        headers: {
          'Origin': 'http://localhost:5173',
          'Access-Control-Request-Method': 'POST',
          'Access-Control-Request-Headers': 'Content-Type'
        }
      });
      
      debugLog('📥 OPTIONS Response received:', optionsResponse);
      debugLog('   Status:', optionsResponse.status, optionsResponse.statusText);
      debugLog('   CORS Headers:', {
        'Access-Control-Allow-Origin': optionsResponse.headers.get('Access-Control-Allow-Origin'),
        'Access-Control-Allow-Methods': optionsResponse.headers.get('Access-Control-Allow-Methods'),
        'Access-Control-Allow-Headers': optionsResponse.headers.get('Access-Control-Allow-Headers')
      });
      
      return {
        success: true,
        message: 'CORS test completed',
        status: optionsResponse.status,
        corsHeaders: {
          'Access-Control-Allow-Origin': optionsResponse.headers.get('Access-Control-Allow-Origin'),
          'Access-Control-Allow-Methods': optionsResponse.headers.get('Access-Control-Allow-Methods'),
          'Access-Control-Allow-Headers': optionsResponse.headers.get('Access-Control-Allow-Headers')
        }
      };
      
    } catch (error) {

      debugError('💥 CORS test failed:', error);
      return {
        success: false,
        message: 'CORS test failed',
        error: error.message,
        type: error.name
      };
    }
  }

  // Test if eligibility endpoint exists on production backend
  async testEligibilityEndpointOnProduction() {
    debugLog('🧪 Testing eligibility endpoint on production backend...');
    
    try {
      // Test with a simple POST request to /v1/eligibility
      debugLog('📤 Testing POST /v1/eligibility on production backend...');
      
      const mockData = {
        profile: {
          industry: 'Technology',
          headcount: 50,
          revenue_range: '1M-10M',
          incorporation_year: 2020,
          singapore_based: true,
          project_type: 'Digital Transformation',
          project_budget: 100000,
          project_timeline: '6-12 months',
          cofunding_capacity: 30,
          available_documents: ['ACRA Business Profile'],
          compliance_declarations: {
            no_outstanding_issues: true,
            meets_local_shareholding: true,
            authorized_to_apply: true
          }
        }
      };
      
      const response = await fetch('https://optra-backend.onrender.com/v1/eligibility', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(mockData)
      });
      
      debugLog('📥 Response received:', response);
      debugLog('   Status:', response.status, response.statusText);
      debugLog('   Headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const data = await response.json();
        debugLog('✅ Eligibility endpoint exists and works:', data);
        return {
          success: true,
          message: 'Eligibility endpoint exists and works',
          status: response.status,
          data: data
        };
      } else {
        // Get the error response body to see what's wrong
        let errorBody = '';
        try {
          errorBody = await response.text();
          debugLog('❌ Error response body:', errorBody);
        } catch (error) {
          debugLog('❌ Could not read error response body:', error);
        }
        
        // Try to parse as JSON
        let errorData = null;
        try {
          errorData = JSON.parse(errorBody);
        } catch (error) {
          debugLog('❌ Error response was not valid JSON:', error);
          // Not JSON, use as plain text
        }
        
        debugLog('❌ Eligibility endpoint responded with error:', response.status);
        debugLog('❌ Error details:', errorData || errorBody);
        
        return {
          success: false,
          message: `Eligibility endpoint error: ${response.status}`,
          status: response.status,
          errorDetails: errorData || errorBody,
          sentData: mockData
        };
      }
      
    } catch (error) {

      debugError('💥 Eligibility endpoint test failed:', error);
      return {
        success: false,
        message: 'Eligibility endpoint test failed',
        error: error.message,
        type: error.name
      };
    }
  }

  // ===== NEW ASYNC ASSESSMENT ENDPOINTS =====
  async assessmentFitGate(formAnswers) {
    const backendUrl = this.getBackendUrls()[0];
    const url = `${backendUrl}/api/assessments/fit-gate`;
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ form_answers: formAnswers }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Fit gate failed (${response.status}): ${errorText}`);
    }
    return response.json();
  }

  async startAssessmentSync(formAnswers, userMeta = null, accessOptions = {}) {
    debugLog('🚀 Starting SYNC assessment (fallback):', formAnswers);
    
    try {
      const backendUrl = this.getBackendUrls()[0];
      const url = `${backendUrl}/api/assessments/start-sync`;
      const controller = new AbortController();
      const timeoutMs = 120000; // 2 minutes for sync call
      
      const timeoutId = setTimeout(() => {
        debugLog(`⏰ Sync request timeout after ${timeoutMs/1000}s`);
        controller.abort();
      }, timeoutMs);
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...this.getChatActorHeaders(),
      };

      const bodyPayload = {
        form_answers: formAnswers,
        user_meta: userMeta,
      };

      debugLog(`📤 POST ${url}`);
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers,
        body: JSON.stringify(bodyPayload),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        debugError(`❌ Sync assessment failed: ${response.status} ${errorText}`);
        if (response.status === 401) throw buildAssessmentAuthError(errorText);
        throw new Error(`Assessment failed: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      debugLog('✅ Sync assessment completed:', result);
      
      return result;
      
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - the backend is taking too long to respond. Please try again.');
      }
      throw error;
    }
  }

  async startAssessment(formAnswers, userMeta = null, accessOptions = {}) {
    const traceId = this.generateTraceId();
    const startTime = Date.now();
    
    debugLog(`[TRACE-${traceId}] 🚀 Starting async assessment job`);
    this.logApiCall(traceId, 'POST', '/api/assessments/start', { form_answers: formAnswers });
    
    try {
      const backendUrl = this.getBackendUrls()[0];
      const url = `${backendUrl}/api/assessments/start`;
      const controller = new AbortController();
      const timeoutMs = 60000; // 60s timeout aligned with backend request window
      
      const timeoutId = setTimeout(() => {
        debugError(`[TRACE-${traceId}] ⏰ Timeout after ${timeoutMs/1000}s`);
        controller.abort();
      }, timeoutMs);

      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Trace-ID': traceId,
        ...this.getChatActorHeaders(),
      };

      const bodyPayload = {
        form_answers: formAnswers,
        user_meta: userMeta,
      };

      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers,
        body: JSON.stringify(bodyPayload),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      
      if (!response.ok) {
        const errorText = await response.text();
        debugError(`[TRACE-${traceId}] ❌ Request failed: ${response.status}`);
        this.logApiError(traceId, new Error(errorText), duration);
        if (response.status === 401) throw buildAssessmentAuthError(errorText);
        throw new Error(`Failed to start assessment (${response.status}): ${errorText}. Please try again or contact support.`);
      }
      
      const data = await response.json();
      this.logApiResponse(traceId, response.status, duration, data);
      debugLog(`[TRACE-${traceId}] ✅ Job created: ${data.job_id}`);
      
      return { ...data, traceId }; // Include traceId in response
      
    } catch (error) {
      const duration = Date.now() - startTime;
      
      if (error.name === 'AbortError') {
        debugError(`[TRACE-${traceId}] ❌ Request timeout after ${duration}ms`);
        this.logApiError(traceId, error, duration);
        throw new Error('Request timeout. The server is taking too long to respond. This may be due to high server load. Please try again in a few minutes.');
      }
      
      this.logApiError(traceId, error, duration);
      throw error;
    }
  }

  /**
   * Upload one supporting document for the private eligibility assessment (session-scoped).
   * @param {string} sessionId UUID from formData.supporting_documents_session_id
   * @param {File} file
   */
  async uploadSupportingDocument(sessionId, file) {
    const traceId = this.generateTraceId();
    const backendUrl = this.getBackendUrls()[0];
    const url = `${backendUrl}/api/assessments/supporting-docs/upload`;
    const fd = new FormData();
    fd.append('session_id', sessionId);
    fd.append('file', file, file.name);

    const headers = { 'X-Trace-ID': traceId, ...this.getChatActorHeaders() };

    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers,
      body: fd,
    });

    if (!response.ok) {
      let detail = `Upload failed (${response.status})`;
      try {
        const j = await response.json();
        if (j.detail) {
          detail = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
        }
      } catch {
        try {
          detail = await response.text();
        } catch {
          /* ignore */
        }
      }
      if (response.status === 405) {
        detail =
          'Document upload is not enabled on this API (405). Deploy the latest backend with supporting-docs upload, or in dev set VITE_USE_LOCAL_BACKEND=true and run the API locally.';
      }
      throw new Error(detail);
    }

    return response.json();
  }

  async getAssessmentStatus(jobId, traceId = null) {
    const trace = traceId || this.generateTraceId();
    const startTime = Date.now();
    const headers = {
      'X-Trace-ID': trace,
      ...this.getChatActorHeaders(),
    };

    try {
      const response = await this.request(`/api/assessments/status/${jobId}`, {
        method: 'GET',
        headers,
        credentials: 'omit',
      });
      
      const duration = Date.now() - startTime;
      this.logApiResponse(trace, 200, duration, response.data);
      
      return { ...response.data, traceId: trace };
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logApiError(trace, error, duration);
      debugError(`[TRACE-${trace}] ❌ Failed to get assessment status for job ${jobId}`);
      throw error;
    }
  }

  async getAdminRecentAssessments(email, limit = 50) {
    const safeLimit = Math.max(1, Math.min(Number(limit) || 50, 100));
    const normalizedEmail = String(email || '').trim().toLowerCase();
    const query = normalizedEmail
      ? `/api/assessments/admin/recent?limit=${safeLimit}&email=${encodeURIComponent(normalizedEmail)}`
      : `/api/assessments/admin/recent?limit=${safeLimit}`;
    try {
      const response = await this.request(query, {
        method: 'GET',
        credentials: 'omit',
        timeoutMs: 30000,
        headers: {
          ...(normalizedEmail ? { 'X-User-Email': normalizedEmail } : {}),
          ...this.getLocalPreviewHeaders(),
        },
      });
      return response?.data || { items: [] };
    } catch (error) {
      const status = Number(error?.meta?.status || 0);
      if (status !== 405 && String(error?.name || '') !== 'MethodNotAllowedError') {
        throw error;
      }
      const fallbackResponse = await this.request('/api/assessments/admin/recent', {
        method: 'POST',
        credentials: 'omit',
        timeoutMs: 30000,
        headers: {
          'Content-Type': 'application/json',
          ...(normalizedEmail ? { 'X-User-Email': normalizedEmail } : {}),
          ...this.getLocalPreviewHeaders(),
        },
        body: JSON.stringify({
          limit: safeLimit,
          email: normalizedEmail || undefined,
        }),
      });
      return fallbackResponse?.data || { items: [] };
    }
  }

  async getAdminRecentVendorIntelligenceRuns(email, limit = 50) {
    if (isLocalPreviewHost()) {
      return { items: [] };
    }

    const safeLimit = Math.max(1, Math.min(Number(limit) || 50, 100));
    const normalizedEmail = String(email || '').trim().toLowerCase();
    const query = normalizedEmail
      ? `/api/vendor-intelligence/admin/recent?limit=${safeLimit}&email=${encodeURIComponent(normalizedEmail)}`
      : `/api/vendor-intelligence/admin/recent?limit=${safeLimit}`;
    try {
      const response = await this.request(query, {
        method: 'GET',
        credentials: 'omit',
        timeoutMs: 30000,
        headers: {
          ...(normalizedEmail ? { 'X-User-Email': normalizedEmail } : {}),
          ...this.getLocalPreviewHeaders(),
        },
      });
      return response?.data || { items: [] };
    } catch (error) {
      const status = Number(error?.meta?.status || 0);
      if (status !== 405 && String(error?.name || '') !== 'MethodNotAllowedError') {
        throw error;
      }
      const fallbackResponse = await this.request('/api/vendor-intelligence/admin/recent', {
        method: 'POST',
        credentials: 'omit',
        timeoutMs: 30000,
        headers: {
          'Content-Type': 'application/json',
          ...(normalizedEmail ? { 'X-User-Email': normalizedEmail } : {}),
          ...this.getLocalPreviewHeaders(),
        },
        body: JSON.stringify({
          limit: safeLimit,
          email: normalizedEmail || undefined,
        }),
      });
      return fallbackResponse?.data || { items: [] };
    }
  }

  async downloadAssessmentPDF(jobId) {
    debugLog('📄 Downloading assessment PDF:', jobId);
    
    try {
      const backendUrls = this.getBackendUrls();
      const url = `${backendUrls[0]}/api/assessments/pdf/${jobId}`;
      
      let attempt = 0;
      const maxAttempts = 5;
      let delay = 1500; // Start with 1.5s
      
      while (attempt < maxAttempts) {
        debugLog(`📄 PDF download attempt ${attempt + 1}/${maxAttempts}`);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/pdf,application/json',
            ...this.getChatActorHeaders(),
          }
        });
        
        const contentType = response.headers.get('content-type') || '';
        
        // Success - PDF ready
        if (response.ok && contentType.includes('application/pdf')) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `OPTRA_Eligibility_${jobId}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(blobUrl);
          
          debugLog('✅ PDF downloaded successfully');
          return { success: true };
        }
        
        // Queued or rate limited - retry with backoff
        if (response.status === 202 || response.status === 429 || contentType.includes('application/json')) {
          const body = await response.json().catch(() => ({}));
          
          // Check for rate limit error
          if (body.error === 'RATE_LIMIT_EXCEEDED' || response.status === 429) {
            const retryAfter = body.retry_after || parseInt(response.headers.get('Retry-After')) || Math.ceil(delay / 1000);
            debugWarn(`⚠️ Rate limited, retrying in ${retryAfter}s`);
            
            // Notify user (you can replace with toast if available)
            if (attempt === 0) {
              console.info(`⏳ Preparing your PDF... retrying in ${retryAfter}s`);
            }
            
            await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
            attempt++;
            delay *= 2; // Exponential backoff
            continue;
          }
          
          // 202 Accepted - PDF being generated
          if (response.status === 202) {
            const retryAfter = body.retry_after || 2;
            debugLog(`⏳ PDF building, retry in ${retryAfter}s`);
            await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
            attempt++;
            delay *= 1.5;
            continue;
          }
        }
        
        // Other error
        throw new Error(`PDF download failed: ${response.status} ${response.statusText}`);
      }
      
      throw new Error('PDF not ready after multiple retries. Please try again later.');
      
    } catch (error) {
      debugError('❌ Failed to download PDF:', error);
      throw error;
    }
  }

  /**
   * Admin: tell the client their results are ready (client-ops event, chat message and email).
   * The message points them at their signed-in dashboard; no public share link is created.
   * Returns { ok, job_id, client_email, client_account_id, notified: { chat, email } }.
   */
  async notifyClientResults(jobId, adminEmail = '') {
    const normalizedEmail = String(adminEmail || getSessionEmail() || '').trim().toLowerCase();
    const response = await this.request('/api/assessments/admin/notify-results', {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...(normalizedEmail ? { 'X-User-Email': normalizedEmail } : {}),
        ...this.getLocalPreviewHeaders(),
      },
      body: JSON.stringify({ job_id: jobId }),
    });
    return response?.data || null;
  }

  async revokeAssessmentShareLink(jobId, adminEmail = '') {
    const normalizedEmail = String(adminEmail || getSessionEmail() || '').trim().toLowerCase();
    const response = await this.request('/api/assessments/admin/share-link/revoke', {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...(normalizedEmail ? { 'X-User-Email': normalizedEmail } : {}),
        ...this.getLocalPreviewHeaders(),
      },
      body: JSON.stringify({ job_id: jobId }),
    });
    return response?.data || { ok: false };
  }

  async getSharedAssessmentByToken(token) {
    const safeToken = String(token || '').trim();
    if (!safeToken) throw new Error('Missing shared token');
    const response = await this.request(`/api/assessments/shared/${encodeURIComponent(safeToken)}`, {
      method: 'GET',
      credentials: 'omit',
      timeoutMs: 30000,
    });
    return response?.data || null;
  }

  // ===== PDF GENERATION =====
  async generatePDF(eligibilityData, templateName = 'eligibility_assessment', includePreflight = true, deterministic = true, outputFilename = null) {
    debugLog('📄 Generating PDF with eligibility data:', eligibilityData);
    
    try {
      // Use the correct PDF generation format based on backend analysis
      const requestBody = {
        content: {
          company_name: eligibilityData.company_name || eligibilityData.company_profile?.companyName || 'Company Name',
          eligibility_score: eligibilityData.overall_score || eligibilityData.score || 0,
          grant_name: eligibilityData.grant_name || 'EDGE',
          eligibility_status: eligibilityData.eligibility_status || 'not-eligible',
          recommendations: eligibilityData.key_recommendations || eligibilityData.recommendations || [],
          next_steps: eligibilityData.next_steps || [],
          risk_factors: eligibilityData.risk_factors || [],
          considerations: eligibilityData.considerations || [],
          estimated_funding: eligibilityData.estimated_funding || 0,
          success_probability: eligibilityData.success_probability || 0,
          requirements_met: eligibilityData.requirements_met || 0,
          requirements_total: eligibilityData.requirements_total || 10,
          assessment_date: new Date().toISOString().split('T')[0],
          confidence_level: eligibilityData.confidence_level || 'low'
        },
        template_name: templateName,
        include_preflight: includePreflight,
        deterministic: deterministic,
        output_filename: outputFilename
      };

      debugLog('📤 Sending PDF generation request to /v1/pdf/generate');
      debugLog('📋 Request body:', requestBody);

      const response = await this.request('/v1/pdf/generate', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      
      debugLog('✅ PDF generation successful:', response);
      
      // The backend returns success, output_path, etc.
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data,
          requestId: response.requestId
        };
      } else {
        throw new Error('PDF generation failed: ' + (response.data?.error || 'Unknown error'));
      }
    } catch (error) {
      debugError('❌ PDF generation failed:', error);
      throw error;
    }
  }

  // Convert eligibility data to markdown format for backend
  convertEligibilityDataToMarkdown(eligibilityData) {
    const {
      reviewId,
      submission_id,
      timestamp,
      overall_eligibility,
      score,
      company_profile,
      project_details,
      eligibility_results,
      recommendations,
      next_steps,
      risk_factors
    } = eligibilityData;

    let markdown = `# Grant Eligibility Assessment Report\n\n`;
    markdown += `**Submission ID:** ${submission_id || reviewId || 'N/A'}\n`;
    markdown += `**Generated:** ${timestamp ? new Date(timestamp).toLocaleDateString() : new Date().toLocaleDateString()}\n`;
    markdown += `**Overall Eligibility:** ${overall_eligibility?.toUpperCase() || 'UNKNOWN'}\n`;
    markdown += `**Overall Score:** ${score || 0}/100\n\n`;

    // Company Profile Section
    if (company_profile) {
      markdown += `## Company Profile\n\n`;
      markdown += `- **Industry:** ${company_profile.industry || 'Not specified'}\n`;
      markdown += `- **Headcount:** ${company_profile.headcount || 'Not specified'}\n`;
      markdown += `- **Revenue Range:** ${company_profile.revenue_range || 'Not specified'}\n`;
      markdown += `- **Singapore Based:** ${company_profile.singapore_based ? 'Yes' : 'No'}\n`;
      if (company_profile.past_grants && company_profile.past_grants.length > 0) {
        markdown += `- **Past Grants:** ${company_profile.past_grants.join(', ')}\n`;
      }
      markdown += `\n`;
    }

    // Project Details Section
    if (project_details) {
      markdown += `## Project Details\n\n`;
      markdown += `- **Project Type:** ${project_details.project_type || 'Not specified'}\n`;
      markdown += `- **Project Budget:** $${(project_details.project_budget || 0).toLocaleString()}\n`;
      markdown += `- **Project Timeline:** ${project_details.project_timeline || 'Not specified'}\n`;
      markdown += `- **Cofunding Capacity:** ${project_details.cofunding_capacity || 0}%\n\n`;
    }

    // Eligibility Results Section
    if (eligibility_results) {
      markdown += `## Grant Eligibility Results\n\n`;
      Object.entries(eligibility_results).forEach(([key, grant]) => {
        markdown += `### ${grant.name || key}\n\n`;
        markdown += `- **Status:** ${grant.eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}\n`;
        markdown += `- **Score:** ${grant.score || 0}/100\n`;
        markdown += `- **Max Amount:** $${(grant.max_amount || 0).toLocaleString()}\n`;
        if (grant.eligible) {
          markdown += `- **Recommended Amount:** $${(grant.recommended_amount || 0).toLocaleString()}\n`;
        }
        markdown += `- **Requirements Met:** ${grant.requirements_met || 0}/${grant.total_requirements || 0}\n`;
        if (grant.notes) {
          markdown += `- **Notes:** ${grant.notes}\n`;
        }
        markdown += `\n`;
      });
    }

    // Recommendations Section
    if (recommendations && recommendations.length > 0) {
      markdown += `## Key Recommendations\n\n`;
      recommendations.forEach((rec, index) => {
        markdown += `${index + 1}. ${rec}\n`;
      });
      markdown += `\n`;
    }

    // Next Steps Section
    if (next_steps && next_steps.length > 0) {
      markdown += `## Next Steps\n\n`;
      next_steps.forEach((step, index) => {
        markdown += `${index + 1}. ${step}\n`;
      });
      markdown += `\n`;
    }

    // Risk Factors Section
    if (risk_factors && risk_factors.length > 0) {
      markdown += `## Risk Factors & Considerations\n\n`;
      risk_factors.forEach((risk, index) => {
        markdown += `${index + 1}. ${risk}\n`;
      });
      markdown += `\n`;
    }

    markdown += `---\n\n`;
    markdown += `*Report generated by OPTRA Grant Eligibility Assessment*\n`;

    return markdown;
  }


  // List available PDF templates
  async getPDFTemplates() {
    try {
      const response = await this.request('/v1/tools/export/templates');
      return response.data;
    } catch (error) {
      debugError('❌ Failed to fetch PDF templates:', error);
      throw error;
    }
  }

  // Generate PDF preview
  async generatePDFPreview(templateId, data, sampleId = null, render = 'both') {
    try {
      const params = new URLSearchParams();
      params.append('render', render);
      if (sampleId) params.append('sampleId', sampleId);

      const response = await this.request(`/v1/tools/export/preview?${params}`, {
        method: 'POST',
        body: JSON.stringify({
          templateId: templateId,
          data: data,
          sampleId: sampleId
        }),
      });
      
      return response.data;
    } catch (error) {
      debugError('❌ PDF preview generation failed:', error);
      throw error;
    }
  }

  // ===== FEEDBACK SYSTEM =====
  async submitGeneralFeedback(feedbackData) {
    debugLog('💬 Submitting general feedback:', feedbackData);
    const payload = { ...feedbackData };
    const safeTrim = (value, max = 4000) => {
      if (typeof value !== 'string') return value ?? null;
      return value.length > max ? value.slice(0, max) : value;
    };
    
    try {
      if (payload.reviewId && !payload.review_id) {
        payload.review_id = payload.reviewId;
      }
      if (payload.pageName && !payload.page_name) {
        payload.page_name = payload.pageName;
      }
      if (payload.grantType && !payload.grant_type) {
        payload.grant_type = payload.grantType;
      }
      if (payload.sessionId && !payload.session_id) {
        payload.session_id = payload.sessionId;
      }
      if (payload.tokenHash && !payload.token_hash) {
        payload.token_hash = payload.tokenHash;
      }
      if (payload.contextTag && !payload.context_tag) {
        payload.context_tag = payload.contextTag;
      }
      if (payload.rating && !payload.thumbs) {
        payload.thumbs = payload.rating === 'up' ? 1 : -1;
      }
      payload.query = safeTrim(payload.query, 2000);
      payload.output = safeTrim(payload.output, 5000);

      const response = await this.deduplicatedRequest('/v1/feedback/', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      
      debugLog('✅ General feedback submitted successfully:', response);
      return response.data;
    } catch (error) {
      debugError('❌ General feedback submission failed:', error);
      
      try {
        const generatedResponseId = this.generateUUID();
        const fallbackPayload = {
          response_id: generatedResponseId,
          session_id: payload.session_id || sessionManager.getSessionId(),
          review_id: feedbackData.reviewId || payload.review_id || payload.page_name || generatedResponseId,
          rating: payload.rating === 'up' ? 5 : 1,
          reason: payload.page_name || payload.context_tag || 'General feedback',
          labels: payload.context_tag ? [payload.context_tag] : (payload.page_name ? [payload.page_name] : []),
          thumbs: payload.rating === 'up' ? 1 : -1,
          notes_text: safeTrim(payload.output || payload.query || null, 2000),
          outcome_flag: 'unknown',
        };

        const fallbackResponse = await this.deduplicatedRequest('/v1/feedback/legacy', {
          method: 'POST',
          body: JSON.stringify(fallbackPayload),
        });

        debugLog('✅ Legacy feedback submission succeeded:', fallbackResponse);
        return fallbackResponse.data;
      } catch (legacyError) {
        debugError('❌ Legacy feedback submission failed:', legacyError);
        throw legacyError;
      }
    }
  }

  async submitReviewFeedback(reviewId, rating, outcomeKnown = false, outcomeLabel = null, notes = null) {
    debugLog('⭐ Submitting review feedback:', { reviewId, rating, outcomeKnown, outcomeLabel, notes });
    
    try {
      const requestBody = {
        reviewId: reviewId,
        rating: rating,
        outcomeKnown: outcomeKnown,
        outcomeLabel: outcomeLabel,
        notes: notes
      };

      const response = await this.deduplicatedRequest('/v1/feedback/review-feedback', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      
      debugLog('✅ Review feedback submitted successfully:', response);
      return response.data;
    } catch (error) {
      debugError('❌ Review feedback submission failed:', error);
      throw error;
    }
  }

  async getFeedbackAnalytics(sessionId = null, timeRange = '7d') {
    try {
      const params = new URLSearchParams();
      if (sessionId) params.append('session_id', sessionId);
      params.append('time_range', timeRange);

      const response = await this.request(`/v1/feedback/analytics?${params}`);
      return response.data;
    } catch (error) {
      debugError('❌ Failed to fetch feedback analytics:', error);
      throw error;
    }
  }

  // ===== META & HEALTH ENDPOINTS =====
  async getMeta() {
    try {
      const response = await this.request('/meta');
      return response.data;
    } catch (error) {
      debugError('❌ Failed to fetch meta data:', error);
      throw error;
    }
  }

  async testCORS() {
    try {
      const response = await this.request('/cors-test');
      return response.data;
    } catch (error) {
      debugError('❌ CORS test failed:', error);
      throw error;
    }
  }
}

// Export the session manager as well
export { sessionManager };

export const getPayload = (resp) => (resp && typeof resp === 'object' && 'data' in resp ? resp.data : resp);

export const apiService = new ApiService();
export default apiService;
// Force Vercel redeploy - Thu Aug 28 18:41:29 +08 2025
